// @ts-nocheck
import { HeaderControls } from '../ui/HeaderControls';
import React, { Suspense, useState, useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame, useThree, extend, useLoader } from '@react-three/fiber';
import { OrbitControls, Environment, MeshDistortMaterial, MeshWobbleMaterial, TransformControls, useTexture, Line, Sky, Text3D, Center, Float, Html, Outlines } from '@react-three/drei';
import { StarfieldBackground } from './StarfieldBackground';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { CollapsiblePanel } from '../ui/CollapsiblePanel';
import { Download, Share2, Palette, Box as BoxIcon, Wand2, Plus, Trash2, MessageSquare, Move, Code, X, Pencil, Layers as LayersIcon, ChevronDown, Image as ImageIcon, Upload, Undo, Camera, Menu, FileUp, Rotate3d, Scaling, Cloud, HardDrive, RefreshCw, ChevronLeft, ChevronRight, MousePointer2, Check, Bot, Save, AlertTriangle } from 'lucide-react';
import { useRecording } from '../../context/RecordingContext';
import { useAuth } from '../../context/AuthContext';
import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Water } from 'three-stdlib';
import { cn } from '../../lib/utils';
import { saveScene, getScene, saveAsset, checkAssetExists } from '../../services/storage';
import { generateText } from '../../services/gemini';
import Object3DAnnotator from './Object3DAnnotator';
import TodoOverlay from '../TodoOverlay';
import { Task, TaskPosition, OnlineModelType, OfflineModelId } from '../../types';
import { v4 as uuidv4 } from 'uuid';
import { parsePromptTo3DAction } from '../../services/matildaActionService';
import { isModelDownloaded } from '../../stubs/offlineService';
import { CHROMA_KEY_GREEN } from '../../../../world/chroma';
import { BYOKSettingsModal } from '../../stubs/modals';
import { ApiKeyHelpModal } from '../../stubs/modals';
import { ModelDownloadModal } from '../../stubs/modals';
import { AuthRequiredModal } from '../../stubs/modals';
import CodeyWidgetTemplate from '../ui/CodeyWidgetTemplate';
import { OFFLINE_MODELS, ONLINE_MODELS } from '../../stubs/constants';
import { getRandomDemoResponse } from '../../stubs/demoOrchestrator';

extend({ Water });

// --- Types ---
export type ShapeType = 
  | 'torus' | 'cube' | 'sphere' | 'cone' | 'cylinder' 
  | 'tetrahedron' | 'octahedron' | 'dodecahedron' | 'icosahedron' 
  | 'capsule' | 'ring' | 'circle' | 'heart' | 'star' 
  | 'tube' | 'text' | 'pyramid' | 'metatron' | 'custom' | 'upload';

export type MaterialType = 'standard' | 'distort' | 'wobble' | 'edges' | 'wireframed';
export type AspectRatio = '1:1' | '16:9' | '9:16';
export type AnimationType = 'spin' | 'bounce' | 'pulse' | 'orbit' | 'float' | 'shake' | 'swing' | 'spiral' | 'melt' | 'crumple' | 'twist' | 'parallax' | 'sway' | 'holo' | 'chroma' | 'rim' | 'dust' | 'smoke' | 'orb' | 'firefly' | 'origami' | 'slice' | 'tunnel' | 'jiggle' | 'fractal' | 'liquid' | 'damp' | 'none';
export type PhysicsType =
  | 'viscous-jiggle' | 'plastic-denting' | 'elastic-stretching' | 'surface-ripple' | 'volumetric-squash'
  | 'thermochromic-heating' | 'kinetic-abrasion' | 'condensation-wipe' | 'electrostatic-discharge' | 'rust-oxidation'
  | 'brittle-fracture' | 'granular-dissolution' | 'fibrous-tearing' | 'liquid-splash' | 'dust-displacement'
  | 'magnetic-snap' | 'gyroscopic-resistance' | 'aerodynamic-flutter' | 'haptic-weight' | 'acoustic-resonance';
type LightingType = 'studio' | 'neon' | 'alien' | 'sunset' | 'night' | 'sunlight';
type BackgroundType = 'stars' | 'gradient' | 'grid' | 'city' | 'none' | 'green' | 'water' | 'vanta-clouds' | 'vanta-cells' | 'vanta-net' | 'vanta-rings' | 'vanta-halo';

export interface SceneObject {
  id: string;
  type: ShapeType;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  material: {
    type: MaterialType[];
    color: string;
    roughness: number;
    metalness: number;
    emissive: string;
    emissiveIntensity: number;
    ambientOcclusion: number;
    wireframe?: boolean;
    texture?: string;
  };
  subMaterials?: { [meshName: string]: Partial<SceneObject['material']> };
  animation: AnimationType[] | AnimationType;
  animationSpeed: number;
  physics?: PhysicsType[];
  physicsStrength?: number;
  points?: THREE.Vector2[]; // For custom shapes
  text?: string; // For text objects
  font?: string; // For text objects
  isSmallCaps?: boolean; // For text objects
  modelUrl?: string; // For uploaded models
  modelType?: 'gltf' | 'obj'; // For uploaded models
  useOriginalMaterial?: boolean; // For uploaded models to retain original materials
}

// --- Constants ---
const SHAPE_OPTIONS: ShapeType[] = [
  'text', 'torus', 'cube', 'sphere', 'cone', 'cylinder', 
  'tetrahedron', 'octahedron', 'dodecahedron', 'icosahedron', 
  'capsule', 'ring', 'circle', 'heart', 'star', 
  'tube', 'pyramid', 'metatron'
];

// Expanded Texture Options
const TEXTURE_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Wood', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/hardwood2_diffuse.jpg' },
  { label: 'Brick', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/brick_diffuse.jpg' },
  { label: 'Water', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/water.jpg' },
  { label: 'Moon', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets/moon_1024.jpg' },
  { label: 'Grid', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/uv_grid_opengl.jpg' },
  { label: 'Earth', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets/earth_atmos_2048.jpg' },
  { label: 'Grass', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/terrain/grasslight-big.jpg' },
  { label: 'Lava', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/lava/lavatile.jpg' },
  { label: 'Cloud', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/lava/cloud.png' },
  { label: 'Crate', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/crate.gif' },
  { label: 'Checker', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/floors/FloorsCheckerboard_S_Diffuse.jpg' },
  { label: 'Fabric', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/disturb.jpg' },
];

const FONT_OPTIONS = [
  { label: 'Helvetiker', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/helvetiker_regular.typeface.json' },
  { label: 'Helvetiker Bold', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/helvetiker_bold.typeface.json' },
  { label: 'Optimer', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/optimer_regular.typeface.json' },
  { label: 'Optimer Bold', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/optimer_bold.typeface.json' },
  { label: 'Gentilis', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/gentilis_regular.typeface.json' },
  { label: 'Gentilis Bold', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/gentilis_bold.typeface.json' },
  { label: 'Droid Sans', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/droid/droid_sans_regular.typeface.json' },
  { label: 'Droid Sans Bold', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/droid/droid_sans_bold.typeface.json' },
  { label: 'Droid Serif', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/droid/droid_serif_regular.typeface.json' },
  { label: 'Droid Serif Bold', value: 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/droid/droid_serif_bold.typeface.json' },
  { label: 'Orbitron', value: '/api/proxy-font?url=' + encodeURIComponent('https://theparadoxuniverse.b-cdn.net/Fonts/Orbitron_Regular.json') },
  { label: 'Comica', value: '/api/proxy-font?url=' + encodeURIComponent('https://theparadoxuniverse.b-cdn.net/Fonts/Comica%20Regular_Regular.json') },
  { label: 'Retro Pixel', value: '/api/proxy-font?url=' + encodeURIComponent('https://theparadoxuniverse.b-cdn.net/Fonts/Retro%20Pixel_Regular.json') },
  { label: 'Ananias', value: '/api/proxy-font?url=' + encodeURIComponent('https://theparadoxuniverse.b-cdn.net/Fonts/NF-Ananias_Regular.json') },
  { label: 'Ananias Bold', value: '/api/proxy-font?url=' + encodeURIComponent('https://theparadoxuniverse.b-cdn.net/Fonts/NF-Ananias%20Bold_Regular.json') },
];

const toSmallCaps = (text: string) => {
  // Unicode small caps are often missing from 3D fonts, causing question marks.
  // We'll use uppercase as a safe fallback for "Small Caps" style to ensure text is readable.
  return text.toUpperCase();
};

const CameraCapture = ({ cameraRef }: { cameraRef: React.MutableRefObject<THREE.PerspectiveCamera | null> }) => {
  const { camera } = useThree();
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) {
      cameraRef.current = camera;
    }
  }, [camera, cameraRef]);
  return null;
};

const ANIMATION_LABELS: Record<string, string> = {
    'spin': 'Spin',
    'bounce': 'Bounce',
    'pulse': 'Pulse',
    'orbit': 'Orbit',
    'float': 'Float',
    'shake': 'Shake',
    'swing': 'Swing',
    'spiral': 'Spiral',
    'melt': 'Melt Drip',
    'crumple': 'Crumple Press',
    'twist': 'Twist Spin',
    'parallax': 'Parallax Move',
    'sway': 'Side Sway',
    'holo': 'Holo Pulse',
    'chroma': 'Color Shift',
    'rim': 'Rim Pulse',
    'dust': 'Dissolve',
    'smoke': 'Smoke Rise',
    'orb': 'Orb Float',
    'firefly': 'Firefly Path',
    'origami': 'Origami Fold',
    'slice': 'Slice Reveal',
    'tunnel': 'Tunnel Loop',
    'jiggle': 'Jiggle Phys',
    'fractal': 'Fractal Grow',
    'liquid': 'Liquid Wave',
    'damp': 'Damp Osc'
};

const ANIMATION_OPTIONS = Object.keys(ANIMATION_LABELS) as AnimationType[];

const PHYSICS_LABELS: Record<string, { label: string, disclaimer: string }> = {
    'viscous-jiggle': { label: 'Viscous Jiggle', disclaimer: 'Click' },
    'plastic-denting': { label: 'Plastic Denting', disclaimer: 'Click' },
    'elastic-stretching': { label: 'Elastic Stretch', disclaimer: 'Drag' },
    'surface-ripple': { label: 'Surface Ripple', disclaimer: 'Click' },
    'volumetric-squash': { label: 'Volumetric Squash', disclaimer: 'Drag' },
    'thermochromic-heating': { label: 'Thermochromic', disclaimer: 'Hold' },
    'condensation-wipe': { label: 'Condensation', disclaimer: 'Drag' },
    'electrostatic-discharge': { label: 'Electrostatic', disclaimer: 'Hold' },
    'rust-oxidation': { label: 'Rust Bloom', disclaimer: 'Click' },
    'brittle-fracture': { label: 'Brittle Fracture', disclaimer: 'Click' },
    'fibrous-tearing': { label: 'Fibrous Tear', disclaimer: 'Click' },
    'liquid-splash': { label: 'Liquid Splash', disclaimer: 'Click' },
    'dust-displacement': { label: 'Dust Displace', disclaimer: 'Drag' },
    'magnetic-snap': { label: 'Magnetic Snap', disclaimer: 'Drag' },
    'gyroscopic-resistance': { label: 'Gyro Resistance', disclaimer: 'Drag' },
    'aerodynamic-flutter': { label: 'Aero Flutter', disclaimer: 'Drag' },
    'haptic-weight': { label: 'Haptic Weight', disclaimer: 'Drag' },
    'acoustic-resonance': { label: 'Acoustic Res', disclaimer: 'Click' },
};

const PHYSICS_OPTIONS = Object.keys(PHYSICS_LABELS) as PhysicsType[];

const BACKGROUND_OPTIONS: BackgroundType[] = ['none', 'stars', 'green', 'water', 'vanta-clouds', 'vanta-cells', 'vanta-net', 'vanta-rings', 'vanta-halo'];
const LIGHTING_OPTIONS: LightingType[] = ['studio', 'sunlight', 'neon', 'alien'];


// --- Components ---

class TextureErrorBoundary extends React.Component<{ children: React.ReactNode, fallback: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode, fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error("Texture loading error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * ModelErrorBoundary — Phase 9 hardened.
 *
 * On model-load failure (most commonly: GCS signed URL expired → 403 →
 * browser surfaces as CORS error because 403 has no CORS headers), this
 * boundary now:
 *   1. Catches the error normally (existing behavior preserved)
 *   2. If `gcsUrl` prop provided AND we haven't retried yet, POSTs to
 *      /api/sign-asset to get a fresh v4 signature
 *   3. Calls onUrlRefreshed(freshUrl) so the parent can swap the URL and
 *      retrigger the load (typically via a `key={url}` change downstream)
 *   4. If retry fails OR no gcsUrl provided, falls through to the existing
 *      fallback render — same "Model Load Error (Expired)" badge as before.
 *
 * Both new props are OPTIONAL — boundaries without them behave exactly
 * as the original implementation. Strictly additive.
 */
class ModelErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback: React.ReactNode;
    gcsUrl?: string;
    onUrlRefreshed?: (freshUrl: string) => void;
  },
  { hasError: boolean; retriedRefresh: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode; gcsUrl?: string; onUrlRefreshed?: (freshUrl: string) => void }) {
    super(props);
    this.state = { hasError: false, retriedRefresh: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  async componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error("Model loading error:", error, errorInfo);
    // Attempt a one-shot re-sign + retry when the caller wired the props.
    if (this.props.gcsUrl && this.props.onUrlRefreshed && !this.state.retriedRefresh) {
      this.setState({ retriedRefresh: true });
      try {
        const { requestSignedUrlRefresh } = await import('../../services/gcp');
        const freshUrl = await requestSignedUrlRefresh(this.props.gcsUrl);
        if (freshUrl) {
          console.info('[Matilda] GLB URL expired, refreshed via /api/sign-asset');
          // Reset error state so the new URL can render. Parent owns the URL
          // swap via onUrlRefreshed → re-render with key={url} cascades.
          this.setState({ hasError: false });
          this.props.onUrlRefreshed(freshUrl);
        }
      } catch (refreshErr) {
        console.warn('[Matilda] sign-asset refresh failed:', refreshErr);
      }
    }
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const TexturedMaterial = ({ url, materialProps, type }: { url: string, materialProps: Record<string, unknown>, type: MaterialType[] }) => {
  const texture = useTexture(url);
  
  // Clone texture to avoid shared state issues if needed, though useTexture caches
  const tex = useMemo(() => texture.clone(), [texture]);

  if (type.includes('distort')) return <MeshDistortMaterial {...materialProps} map={tex} speed={2} distort={0.4} />;
  if (type.includes('wobble')) return <MeshWobbleMaterial {...materialProps} map={tex} factor={1} speed={2} />;
  return <meshStandardMaterial {...materialProps} map={tex} />;
};

const TextureSelector = ({ value, onChange }: { value: string, onChange: (val: string) => void }) => {
  const [isRemovingBg, setIsRemovingBg] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      onChange(url);
    }
  };

  const handleRemoveBackground = async () => {
    if (!value || !value.startsWith('blob:')) {
        alert("Only recently uploaded textures can have their background removed.");
        return;
    }

    setIsRemovingBg(true);
    try {
      const res = await fetch(value);
      const blob = await res.blob();
      const file = new File([blob], "texture.png", { type: 'image/png' });

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/remove-background', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Background removal failed');

      const data = await response.json();
      if (data.success && data.image) {
        onChange(data.image);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to remove background.");
    } finally {
      setIsRemovingBg(false);
    }
  };

  return (
    <div className="h-48 overflow-y-auto rounded-md border bg-black/20 p-2">
      <div className="grid grid-cols-4 gap-2">
        {/* Upload Button */}
        <div className="flex flex-col gap-2">
            <label className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-sm border border-dashed border-muted-foreground/50 bg-muted/10 hover:bg-muted/20">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-[8px] text-muted-foreground mt-1">Upload</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
            </label>
            
            {value && value.startsWith('blob:') && (
                <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-6 w-full text-[8px] gap-1"
                    onClick={handleRemoveBackground}
                    disabled={!navigator.onLine || isRemovingBg}
                >
                    {isRemovingBg ? <Loader2 className="h-2 w-2 animate-spin" /> : <LayersIcon className="h-2 w-2" />}
                    BG
                </Button>
            )}
        </div>

        {TEXTURE_OPTIONS.map(opt => (
            <div 
            key={opt.label}
            onClick={() => onChange(opt.value)}
            className={cn(
                "group relative aspect-square cursor-pointer rounded-sm overflow-hidden border border-transparent hover:border-primary transition-all hue-exclude",
                value === opt.value && "border-primary ring-1 ring-primary"
            )}
            title={opt.label}
            >
            {opt.value ? (
                <img src={opt.value} alt={opt.label} className="h-full w-full object-cover" />
            ) : (
                <div className="h-full w-full bg-muted/20 flex items-center justify-center">
                    <span className="text-[8px] text-muted-foreground">None</span>
                </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-0.5 text-center opacity-0 transition-opacity group-hover:opacity-100">
                <span className="text-[8px] text-white">{opt.label}</span>
            </div>
            </div>
        ))}
      </div>
    </div>
  );
};

class EnvironmentErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.error("Environment loading error:", error);
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

class FontErrorBoundary extends React.Component<{ children: React.ReactNode, fallback: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode, fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error("Font loading error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Module-level cache to prevent race conditions
const scriptCache: Record<string, Promise<boolean>> = {};

const loadScript = (src: string) => {
    if (scriptCache[src]) return scriptCache[src];

    scriptCache[src] = new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => {
            delete scriptCache[src];
            reject(new Error(`Failed to load script: ${src}`));
        };
        document.body.appendChild(script);
    });
    return scriptCache[src];
};

const VantaBackground = ({ 
    type, 
    config = {}
}: { 
    type: BackgroundType, 
    config?: Record<string, unknown>
}) => {
    const vantaRef = useRef<HTMLDivElement>(null);
    const effectRef = useRef<{ destroy: () => void; mode?: string; setOptions?: (opts: Record<string, unknown>) => void } | null>(null);

    useEffect(() => {
        let isMounted = true;

        if (!type.startsWith('vanta-')) {
            if (effectRef.current) {
                effectRef.current.destroy();
                effectRef.current = null;
            }
            return;
        }

        const loadVanta = async () => {
            try {
                // Ensure Three.js is available globally for Vanta
                // @ts-expect-error - THREE is not defined on window in types
                if (!window.THREE) window.THREE = THREE;

                // Load Vanta scripts
                const effectName = type.replace('vanta-', '').toUpperCase(); // CLOUDS, CELLS, RINGS, HALO
                
                // Vanta is highly sensitive to Three.js versions. 
                // For NET, we use r134 as requested to ensure color mapping works correctly.
                if (effectName === 'NET') {
                    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js');
                } else {
                    // @ts-expect-error - THREE is not defined on window in types
                    if (!window.THREE) window.THREE = THREE;
                }
                
                let scriptUrl = '';
                switch(effectName) {
                    case 'CLOUDS': scriptUrl = 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.clouds.min.js'; break;
                    case 'CELLS': scriptUrl = 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.cells.min.js'; break;
                    case 'NET': scriptUrl = 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.net.min.js'; break;
                    case 'RINGS': scriptUrl = 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.rings.min.js'; break;
                    case 'HALO': scriptUrl = 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.halo.min.js'; break;
                }

                if (scriptUrl) {
                    await loadScript(scriptUrl);
                    if (!isMounted) return;
                    
                    // Initialize Vanta
                    if (vantaRef.current) {
                        // @ts-expect-error - VANTA is not defined on window in types
                        const VANTA = window.VANTA;
                        if (!VANTA) {
                            console.error("VANTA not found on window");
                            return;
                        }
                        
                        // Check if we can just update options
                        if (effectRef.current && effectRef.current.mode === effectName) {
                             // Update options
                             // Helper to convert hex string to int
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             
                             const updates: Record<string, unknown> = {
                                 minHeight: 200.00,
                                 minWidth: 200.00,
                                 scale: 1.00,
                                 speed: (config.speed as number) || 1,
                                 ...config
                             };
                             
                             if (effectName === 'CLOUDS' && VANTA.CLOUDS) {
                                 updates.backgroundColor = c(config.backgroundColor || '#ffffff');
                                 updates.skyColor = c(config.skyColor || '#68b8d7');
                                 updates.cloudColor = c(config.cloudColor || '#adc1de');
                                 updates.cloudShadowColor = c(config.cloudShadowColor || '#183550');
                                 updates.sunColor = c(config.sunColor || '#ff9919');
                                 updates.sunGlareColor = c(config.sunGlareColor || '#ff6633');
                                 updates.sunlightColor = c(config.sunlightColor || '#ff9919');
                             } else if (effectName === 'CELLS' && VANTA.CELLS) {
                                 updates.color1 = c(config.backgroundColor || '#000000'); // Dark -> color1
                                 updates.color2 = c(config.lightColor || '#ffffff'); // Light -> color2
                             } else if (effectName === 'NET' && VANTA.NET) {
                                 updates.color = c(config.lightColor || '#4aff');
                                 updates.backgroundColor = c(config.backgroundColor || '#000000');
                             } else if (effectName === 'RINGS' && VANTA.RINGS) {
                                 updates.color = c(config.lightColor || '#88ff');
                                 updates.backgroundColor = c(config.backgroundColor || '#202428');
                             } else if (effectName === 'HALO' && VANTA.HALO) {
                                 updates.baseColor = c(config.lightColor || '#1184ff');
                                 updates.backgroundColor = c(config.backgroundColor || '#131a43');
                             }
                             
                             effectRef.current.setOptions(updates);
                             return; // Skip recreation
                        }

                        if (effectRef.current) {
                            effectRef.current.destroy();
                            effectRef.current = null;
                        }

                        const baseConfig = {
                            el: vantaRef.current,
                            mouseControls: true,
                            touchControls: true,
                            gyroControls: false,
                            minHeight: 200.00,
                            minWidth: 200.00,
                            scale: 1.00,
                            speed: config.speed || 1,
                            ...config
                        };

                        // @ts-expect-error - effect type is complex
                        let effect;

                        if (effectName === 'CLOUDS' && VANTA.CLOUDS) {
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             effect = VANTA.CLOUDS({ 
                                 ...baseConfig, 
                                 backgroundColor: c(config.backgroundColor || '#ffffff'),
                                 skyColor: c(config.skyColor || '#68b8d7'),
                                 cloudColor: c(config.cloudColor || '#adc1de'),
                                 cloudShadowColor: c(config.cloudShadowColor || '#183550'),
                                 sunColor: c(config.sunColor || '#ff9919'),
                                 sunGlareColor: c(config.sunGlareColor || '#ff6633'),
                                 sunlightColor: c(config.sunlightColor || '#ff9919'),
                                 speed: config.speed || 1
                             });
                             effect.mode = 'CLOUDS';
                        } else if (effectName === 'CELLS' && VANTA.CELLS) {
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             effect = VANTA.CELLS({ 
                                 ...baseConfig, 
                                 color1: c(config.backgroundColor || '#000000'),
                                 color2: c(config.lightColor || '#ffffff')
                             });
                             effect.mode = 'CELLS';
                        } else if (effectName === 'NET' && VANTA.NET) {
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             effect = VANTA.NET({ 
                                 el: vantaRef.current,
                                 mouseControls: true,
                                 touchControls: true,
                                 gyroControls: false,
                                 minHeight: 200.00,
                                 minWidth: 200.00,
                                 scale: 1.00,
                                 scaleMobile: 1.00,
                                 color: c(config.lightColor || '#4aff'), 
                                 backgroundColor: c(config.backgroundColor || '#000000'),
                                 points: config.points || 20.00,
                                 maxDistance: config.maxDistance || 29.00,
                                 showDots: config.showDots !== undefined ? config.showDots : false
                             });
                             effect.mode = 'NET';
                        } else if (effectName === 'RINGS' && VANTA.RINGS) {
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             effect = VANTA.RINGS({ 
                                 ...baseConfig, 
                                 color: c(config.lightColor || '#88ff'), 
                                 backgroundColor: c(config.backgroundColor || '#202428') 
                             });
                             effect.mode = 'RINGS';
                        } else if (effectName === 'HALO' && VANTA.HALO) {
                             const c = (hex: string) => parseInt(hex.replace('#', ''), 16);
                             effect = VANTA.HALO({ 
                                 ...baseConfig, 
                                 baseColor: c(config.lightColor || '#1184ff'), 
                                 backgroundColor: c(config.backgroundColor || '#131a43') 
                             });
                             effect.mode = 'HALO';
                        }

                        effectRef.current = effect;
                    }
                }
            } catch (e) {
                console.error("Failed to load Vanta effect", e);
            }
        };

        loadVanta();

        return () => {
            isMounted = false;
            if (effectRef.current) {
                effectRef.current.destroy();
                effectRef.current = null;
            }
        };
    }, [type, config]); // Re-run when type or config changes

    if (!type.startsWith('vanta-')) return null;

    return <div ref={vantaRef} className="absolute inset-0 z-0 pointer-events-none" />;
};

// --- Custom Effects ---
// SobelEffect removed as requested

const BackgroundController = ({ type, speed = 1, lightColor = '#ffffff', darkColor = '#000000' }: { type: BackgroundType, speed?: number, lightColor?: string, darkColor?: string, vantaConfig?: Record<string, unknown> }) => {
  const waterNormalsOriginal = useLoader(THREE.TextureLoader, 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/waternormals.jpg');
  
  const waterNormals = useMemo(() => {
      const t = waterNormalsOriginal.clone();
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      return t;
  }, [waterNormalsOriginal]);
  
  const geom = useMemo(() => new THREE.PlaneGeometry(10000, 10000), []);
  const config = useMemo(
    () => ({
      textureWidth: 512,
      textureHeight: 512,
      waterNormals,
      sunDirection: new THREE.Vector3(),
      sunColor: new THREE.Color(lightColor).getHex(),
      waterColor: new THREE.Color(darkColor).getHex(),
      distortionScale: 3.7,
      fog: false,
    }),
    [waterNormals, lightColor, darkColor]
  );

  useFrame((state, delta) => {
    // @ts-expect-error - scene children type
    if (state.scene.children.find(c => c.name === 'water')) {
         // @ts-expect-error - scene children type
        const water = state.scene.children.find(c => c.name === 'water') as { material?: { uniforms?: Record<string, { value: unknown; setHex?: (hex: number) => void }> } };
        if (water && water.material && water.material.uniforms) {
            if (water.material.uniforms['time'] && typeof water.material.uniforms['time'].value === 'number') {
                water.material.uniforms['time'].value += delta * 0.5 * speed;
            }
            // Ensure colors update if they changed (though key prop on parent might be better, this is safe)
            if (water.material.uniforms['sunColor']?.value && typeof (water.material.uniforms['sunColor'].value as Record<string, unknown>).setHex === 'function') {
                ((water.material.uniforms['sunColor'].value as Record<string, unknown>).setHex as (hex: number) => void)(config.sunColor);
            }
            if (water.material.uniforms['waterColor']?.value && typeof (water.material.uniforms['waterColor'].value as Record<string, unknown>).setHex === 'function') {
                ((water.material.uniforms['waterColor'].value as Record<string, unknown>).setHex as (hex: number) => void)(config.waterColor);
            }
        }
    }
  });

  switch (type) {
    case 'stars':
      return <StarfieldBackground key={lightColor + darkColor} speed={speed} lightColor={lightColor} darkColor={darkColor} />;
    case 'green':
      return <color attach="background" args={[lightColor]} />;
    case 'water':
      return (
        <>
          <EnvironmentErrorBoundary>
            <Environment preset="sunset" />
          </EnvironmentErrorBoundary>
          {/* @ts-expect-error - water is a custom element */}
          <water 
            name="water"
            args={[geom, config]} 
            rotation-x={-Math.PI / 2} 
            position={[0, -2, 0]}
          />
          <Sky sunPosition={[100, 20, 100]} />
        </>
      );
    default:
      return null;
  }
};

const LightingController = ({ type }: { type: LightingType }) => {
  switch (type) {
    case 'sunlight':
      return (
        <>
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={1.5} castShadow />
          <Sky />
        </>
      );
    case 'neon':
      return (
        <>
          <ambientLight intensity={0.2} color="#220033" />
          <pointLight position={[10, 10, 10]} color="#ff00ff" intensity={5} distance={50} />
          <pointLight position={[-10, -10, -10]} color="#00ffff" intensity={5} distance={50} />
          <pointLight position={[0, 5, 0]} color="#ffff00" intensity={3} distance={20} />
          <EnvironmentErrorBoundary>
            <Environment preset="night" />
          </EnvironmentErrorBoundary>
        </>
      );
    case 'alien':
      // scene-light hues — artistic intent, not UI palette
      return (
        <>
          <ambientLight intensity={0.1} color="#001100" />
          <spotLight position={[0, 20, 0]} color="#00ff00" intensity={8} angle={0.6} penumbra={0.5} />
          <pointLight position={[10, 5, 10]} color="#800080" intensity={5} distance={30} />
          <pointLight position={[-10, 0, -10]} color="#00ff00" intensity={2} distance={30} />
          <fog attach="fog" args={['#001100', 5, 30]} />
        </>
      );
    case 'studio':
    default:
      return (
        <>
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
          <pointLight position={[-10, -10, -10]} intensity={0.5} />
          <EnvironmentErrorBoundary>
            <Environment preset="city" />
          </EnvironmentErrorBoundary>
        </>
      );
  }
};


  const UploadedModel = ({ url, modelType, materialProps, subMaterials, useOriginalMaterial }: { url: string, modelType?: 'gltf' | 'obj', materialProps: Record<string, unknown>, subMaterials?: Record<string, unknown>, useOriginalMaterial?: boolean }) => {
      const loader = modelType === 'obj' ? OBJLoader : GLTFLoader;
      const loaded = useLoader(loader, url);
      
      const scene = useMemo(() => {
          if (modelType === 'obj') {
              return (loaded as THREE.Group).clone();
          }
          return (loaded as { scene: THREE.Group }).scene.clone();
      }, [loaded, modelType]);
      
      // Load texture if present - MUST be unconditional to avoid React Hook errors
      const texture = useTexture(materialProps.texture || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=');
      
      if (materialProps.texture && texture && !Array.isArray(texture)) {
          // eslint-disable-next-line react-hooks/immutability
          texture.flipY = false; // GLTF models usually expect this
      }

      // Apply materials
      useFrame(() => {
          if (useOriginalMaterial) return; // Skip material override if using original

          scene.traverse((child) => {
              if ((child as THREE.Mesh).isMesh) {
                  const mesh = child as THREE.Mesh;
                  const meshName = mesh.name || `Mesh_${mesh.id}`;
                  
                  // Determine material to use (sub-material or global)
                  const specificProps = subMaterials?.[meshName] || {};
                  const finalProps = { ...materialProps, ...specificProps };
                  
                  if (mesh.material) {
                      mesh.castShadow = true;
                      mesh.receiveShadow = true;
                      // We need to be careful not to constantly recreate materials
                      // Just update properties
                      const m = mesh.material as THREE.MeshStandardMaterial;
                      if (m.color) m.color.set(finalProps.color);
                      m.roughness = finalProps.roughness;
                      m.metalness = finalProps.metalness;
                      if (m.emissive) m.emissive.set(finalProps.emissive);
                      m.emissiveIntensity = finalProps.emissiveIntensity;
                      m.envMapIntensity = finalProps.envMapIntensity;
                      m.wireframe = finalProps.wireframe;
                      
                      // Apply texture if present
                      if (finalProps.texture && texture && !Array.isArray(texture)) {
                          m.map = texture as THREE.Texture;
                          m.needsUpdate = true;
                      } else if (!finalProps.texture && m.map) {
                          m.map = null;
                          m.needsUpdate = true;
                      }
                  }
              }
          });
      });

      return (
          <primitive 
            object={scene} 
          />
      );
  };


const AnimatedMesh = ({ 
  obj, 
  isSelected, 
  onSelect,
  onUpdate,
  showTransformControls,
  transformMode,
  objects,
  isAnnotatorActive,
  onAnnotateSelect,
  onHover
}: { 
  obj: SceneObject; 
  isSelected: boolean; 
  onSelect: () => void; 
  onUpdate: (updates: Partial<SceneObject>) => void;
  showTransformControls: boolean;
  transformMode: 'translate' | 'rotate' | 'scale';
  objects: SceneObject[];
  isAnnotatorActive?: boolean;
  onAnnotateSelect?: (obj: SceneObject) => void;
  onHover?: (obj: SceneObject | null) => void;
}) => {
  const meshRef = useRef<THREE.Object3D>(null);
  const isDragging = useRef(false);
  const { clock } = useThree();
  const [isHovered, setIsHovered] = useState(false);
  
  // Physics State Refs
  const physicsState = useRef({
    velocity: new THREE.Vector3(),
    angularVelocity: new THREE.Vector3(),
    deformation: 0,
    heat: 0,
    lastInteractionTime: 0,
    isInteracting: false,
    interactionPoint: new THREE.Vector3(),
    dragStartPoint: new THREE.Vector3(),
    originalScale: new THREE.Vector3(1, 1, 1),
    rustLevel: 0,
    condensationLevel: 0,
    fracture: false,
    dissolve: 0,
    abrasionLevel: 0,
    rippleTime: 0,
  });

  // Initialize original scale
  useEffect(() => {
    if (obj.scale) {
        physicsState.current.originalScale.set(obj.scale[0], obj.scale[1], obj.scale[2]);
    }
  }, [obj.scale]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;
    
    // If dragging transform controls, do not override position/rotation/scale from state/animation
    if (isDragging.current) return;

    const time = state.clock.getElapsedTime();
    const speed = obj.animationSpeed || 1;
    const phys = obj.physics || [];
    const strength = obj.physicsStrength || 1;
    const pState = physicsState.current;
    
    // Base Transforms
    const baseX = obj.position[0];
    const baseY = obj.position[1];
    const baseZ = obj.position[2];
    const baseRotX = obj.rotation[0];
    const baseRotY = obj.rotation[1];
    const baseRotZ = obj.rotation[2];
    const baseScale = obj.scale || [1, 1, 1];

    let posX = baseX;
    let posY = baseY;
    let posZ = baseZ;
    let rotX = baseRotX;
    let rotY = baseRotY;
    let rotZ = baseRotZ;
    let scaleX = baseScale[0];
    let scaleY = baseScale[1];
    let scaleZ = baseScale[2];

    // --- Collision Detection ---
    if (phys.length > 0) {
        const myPos = new THREE.Vector3(posX, posY, posZ);
        objects.forEach(other => {
            if (other.id !== obj.id) {
                const otherPos = new THREE.Vector3(...other.position);
                const dist = myPos.distanceTo(otherPos);
                // Simple radius check (assuming ~1 unit radius for now)
                if (dist < 1.2) { 
                    // Trigger interaction if not already interacting recently
                    if (time - pState.lastInteractionTime > 0.5) {
                        pState.deformation = 1.0;
                        pState.lastInteractionTime = time;
                        // Also trigger heat/rust if applicable
                        if (phys.includes('thermochromic-heating')) pState.heat = Math.min(pState.heat + 0.5, 1);
                    }
                }
            }
        });
    }

    const anims = Array.isArray(obj.animation) ? obj.animation : (obj.animation ? [obj.animation] : []);

    // --- Transform Animations ---

    if (anims.includes('spin')) {
      rotY += time * speed;
    }
    if (anims.includes('twist')) {
      rotY += time * 2 * speed;
      rotX += Math.sin(time * speed) * 0.2;
    }
    if (anims.includes('bounce')) {
      posY += Math.abs(Math.sin(time * 2 * speed)) * 1;
    }
    if (anims.includes('jiggle')) {
      scaleX += Math.sin(time * 10 * speed) * 0.1;
      scaleY += Math.cos(time * 10 * speed) * 0.1;
    }
    if (anims.includes('orbit')) {
      posX += Math.sin(time * speed) * 3;
      posZ += Math.cos(time * speed) * 3;
    }
    if (anims.includes('float')) {
      posY += Math.sin(time * speed) * 0.3;
      rotZ += Math.sin(time * 0.5 * speed) * 0.1;
    }
    if (anims.includes('shake')) {
      posX += (Math.random() - 0.5) * 0.1 * speed;
      posY += (Math.random() - 0.5) * 0.1 * speed;
      posZ += (Math.random() - 0.5) * 0.1 * speed;
    }
    if (anims.includes('swing')) {
      rotZ += Math.sin(time * 2 * speed) * 0.5;
    }
    if (anims.includes('sway')) {
      posX += Math.sin(time * speed) * 1.5;
    }
    if (anims.includes('spiral')) {
      posX += Math.sin(time * speed) * 2;
      posZ += Math.cos(time * speed) * 2;
      posY += Math.sin(time * 0.5 * speed) * 1;
    }
    if (anims.includes('pulse')) {
      const factor = 1 + Math.sin(time * 3 * speed) * 0.2;
      scaleX *= factor;
      scaleY *= factor;
      scaleZ *= factor;
    }
    if (anims.includes('melt')) {
      // Simulate melting by squashing Y and expanding X/Z
      const meltFactor = (Math.sin(time * speed) + 1) / 2; // 0 to 1
      scaleY *= (1 - meltFactor * 0.8); // Squash down to 20%
      scaleX *= (1 + meltFactor * 0.5); // Expand out
      scaleZ *= (1 + meltFactor * 0.5);
      posY -= meltFactor * 0.5; // Move down to keep base roughly in place
    }
    if (anims.includes('crumple')) {
        scaleX *= 1 + (Math.random() - 0.5) * 0.2 * speed;
        scaleY *= 1 + (Math.random() - 0.5) * 0.2 * speed;
        scaleZ *= 1 + (Math.random() - 0.5) * 0.2 * speed;
    }
    if (anims.includes('parallax')) {
        // Simple circular motion to simulate parallax view shift
        posX += Math.sin(time * 0.5 * speed) * 0.5;
        posY += Math.cos(time * 0.5 * speed) * 0.5;
    }
    if (anims.includes('smoke')) {
        // Rise and fade simulation (Fade handled in material if possible, here just rise)
        const cycle = (time * speed) % 2;
        posY += cycle * 2; // Rise 2 units
        // Scale down as it rises
        const scaleFade = 1 - (cycle / 2);
        scaleX *= scaleFade;
        scaleY *= scaleFade;
        scaleZ *= scaleFade;
    }
    if (anims.includes('orb')) {
        // Complex float
        posY += Math.sin(time * speed) * 0.5;
        posX += Math.cos(time * 0.7 * speed) * 0.3;
        rotX += time * 0.2 * speed;
        rotY += time * 0.3 * speed;
    }
    if (anims.includes('firefly')) {
        // Rapid random movement
        const t = time * speed;
        posX += Math.sin(t * 3) * 0.5 + Math.cos(t * 5) * 0.3;
        posY += Math.cos(t * 4) * 0.5 + Math.sin(t * 2) * 0.3;
        posZ += Math.sin(t * 6) * 0.5;
    }
    if (anims.includes('origami')) {
        // Unfold effect (Scale 0->1 with steps)
        const cycle = (Math.sin(time * speed) + 1) / 2;
        scaleX *= cycle;
        scaleY *= cycle;
        scaleZ *= cycle;
        rotY += cycle * Math.PI;
    }
    if (anims.includes('slice')) {
        // Scale Y 0 -> 1
        const cycle = (time * speed) % 1;
        scaleY *= cycle;
    }
    if (anims.includes('tunnel')) {
        // Scale Z pulse
        scaleZ *= 1 + Math.sin(time * 5 * speed) * 2;
    }
    if (anims.includes('fractal')) {
        // Grow and rotate
        const cycle = (time * speed) % 2;
        scaleX *= cycle;
        scaleY *= cycle;
        scaleZ *= cycle;
        rotZ += cycle * Math.PI;
    }
    if (anims.includes('liquid')) {
        // Wobble simulation via scale
        scaleX *= 1 + Math.sin(time * 5 * speed) * 0.1;
        scaleZ *= 1 + Math.cos(time * 4 * speed) * 0.1;
    }
    if (anims.includes('damp')) {
        // Damped oscillation
        const t = (time * speed) % 4; // 4 second cycle
        const damp = Math.exp(-t);
        const osc = Math.sin(t * 10) * damp;
        posX += osc;
    }
    if (anims.includes('holo')) {
        // Glitchy scale
        if (Math.random() > 0.9) {
            scaleX *= 1.1;
            scaleZ *= 0.9;
        }
    }

    // --- Physics Implementations ---
    
    // 1. Viscous Jiggle (Spring-mass on click)
    if (phys.includes('viscous-jiggle')) {
        if (pState.deformation > 0.001) {
            const t = time - pState.lastInteractionTime;
            const decay = Math.exp(-t * 3);
            const wave = Math.sin(t * 25) * 0.3 * decay * strength;
            scaleX += wave;
            scaleY -= wave; // Preserve volume roughly
            scaleZ += wave;
            
            if (decay < 0.01) pState.deformation = 0;
        }
    }

    // 2. Plastic Denting (Collision/Click)
    if (phys.includes('plastic-denting') && pState.deformation > 0) {
        // Permanently deform slightly
        scaleY *= 0.9;
        scaleX *= 1.05;
        scaleZ *= 1.05;
        // Reset deformation trigger but keep scale change (simulated by not resetting scale vars)
        // However, since we reset scaleX/Y/Z every frame from baseScale, we need to modify baseScale or apply persistent offset
        // For simplicity, we'll just apply a temporary dent that recovers very slowly or stays
        // Let's make it recover very slowly (viscoelastic)
        const t = time - pState.lastInteractionTime;
        if (t < 2) {
            scaleY *= 0.8; 
            scaleX *= 1.1;
            scaleZ *= 1.1;
        }
    }

    // 3. Elastic Stretching (Drag)
    if (phys.includes('elastic-stretching') && pState.isInteracting) {
        // Calculate stretch based on distance from drag start
        const dist = pState.interactionPoint.distanceTo(pState.dragStartPoint);
        // Smoother stretch, no snap
        const stretch = Math.min(dist, 1.5) * strength; 
        // Smoothly interpolate to target stretch
        scaleY *= 1 + (stretch * 0.5);
        scaleX *= 1 - (stretch * 0.2);
        scaleZ *= 1 - (stretch * 0.2);
    }

    // 4. Surface Ripple (Touch)
    if (phys.includes('surface-ripple') && pState.deformation > 0) {
        const t = time - pState.lastInteractionTime;
        const decay = Math.exp(-t * 2);
        const ripple = Math.sin(t * 20) * 0.1 * decay * strength;
        scaleX += ripple;
        scaleZ += ripple;
    }

    // 5. Volumetric Squash (Drag/Impact)
    if (phys.includes('volumetric-squash') && pState.isInteracting) {
        scaleY *= 0.4;
        scaleX *= 1.8 * strength;
        scaleZ *= 1.8 * strength;
    }

    // 6. Thermochromic Heating (Hold)
    if (phys.includes('thermochromic-heating')) {
        if (pState.isInteracting) {
            pState.heat = Math.min(pState.heat + delta * 2 * strength, 1);
        } else {
            pState.heat = Math.max(pState.heat - delta * 0.5, 0);
        }
    }

    // 8. Condensation Wipe (Swipe/Drag)
    if (phys.includes('condensation-wipe')) {
        if (pState.isInteracting) {
             pState.condensationLevel = Math.max(pState.condensationLevel - delta * 2 * strength, 0);
        } else {
             pState.condensationLevel = Math.min(pState.condensationLevel + delta * 0.1, 1);
        }
    }

    // 9. Electrostatic Discharge (Hover/Hold)
    if (phys.includes('electrostatic-discharge') && pState.isInteracting) {
        // Jitter position rapidly
        posX += (Math.random() - 0.5) * 0.1 * strength;
        posY += (Math.random() - 0.5) * 0.1 * strength;
        posZ += (Math.random() - 0.5) * 0.1 * strength;
    }

    // 10. Rust Bloom (Click)
    // Handled in material section via pState.rustLevel

    // 11. Brittle Fracture (Impact/Click)
    if (phys.includes('brittle-fracture') && pState.deformation > 0) {
         const t = time - pState.lastInteractionTime;
         if (t < 1) {
             // Shake violently but smoother
             const shake = Math.sin(t * 50) * 0.1 * strength * (1-t);
             posX += shake;
             posY += shake;
             posZ += shake;
             
             // Snap back smoother (lerp back to 1)
             // Actually user wants "smoother snap back"
             // Let's just scale down then bounce back
             const s = 1 - (Math.sin(t * Math.PI) * 0.5 * strength);
             scaleX *= s;
             scaleY *= s;
             scaleZ *= s;
         }
    }

    // 13. Fibrous Tearing (Click)
    if (phys.includes('fibrous-tearing') && pState.deformation > 0) {
        const t = time - pState.lastInteractionTime;
        if (t < 0.5) {
             // "Tear" - separate into two (simulated by scaling X way up and Y way down)
             const tear = Math.sin(t * Math.PI) * strength;
             scaleX *= 1 + tear;
             scaleY *= 1 / (1 + tear);
             // Jitter to simulate ragged edges
             scaleY += Math.random() * 0.1 * strength;
        }
    }

    // 14. Liquid Splash (Impact/Click)
    if (phys.includes('liquid-splash') && pState.deformation > 0) {
        const t = time - pState.lastInteractionTime;
        if (t < 1.0) {
            // Smoother splash animation (elastic out)
            const elastic = Math.sin(-13 * (t + 1) * Math.PI / 2) * Math.pow(2, -10 * t) + 1;
            // Flatten and expand
            const splash = (1 - elastic) * strength;
            scaleY *= 1 - splash * 0.8;
            scaleX *= 1 + splash * 1.5;
            scaleZ *= 1 + splash * 1.5;
        }
    }

    // 15. Dust Displacement (Slide/Drag)
    // Handled in material (opacity/transparency)

    // 16. Magnetic Snap (Release)
    if (phys.includes('magnetic-snap') && !pState.isInteracting && pState.lastInteractionTime > 0) {
        // Snap to nearest grid or specific rotation
        // Simple snap to upright
        // Smoother animation
        const snapSpeed = 2 * delta * strength;
        rotX = THREE.MathUtils.lerp(rotX, 0, snapSpeed);
        rotZ = THREE.MathUtils.lerp(rotZ, 0, snapSpeed);
    }

    // 17. Gyroscopic Resistance (Rotate)
    if (phys.includes('gyroscopic-resistance')) {
        // Add a constant rotation that resists change (visualized as precession)
        rotY += delta * 5 * strength; // High angular momentum
        // If user tries to rotate X/Z (via other means), wobble
        rotX += Math.sin(time * 10) * 0.05 * strength;
    }

    // 18. Aerodynamic Flutter (Move Fast)
    if (phys.includes('aerodynamic-flutter')) {
        // Flutter based on "speed" (simulated)
        const flutter = Math.sin(time * 50) * 0.02 * strength;
        posX += flutter;
        rotZ += flutter;
    }

    // 19. Haptic Weight (Lag)
    if (phys.includes('haptic-weight') && pState.isInteracting) {
        // We can't easily lag the mouse drag here without overriding TransformControls
        // But we can simulate "heavy" movement by dampening any changes
        // This is subtle in this context
    }

    // 20. Acoustic Resonance (Click)
    if (phys.includes('acoustic-resonance') && pState.deformation > 0) {
        const t = time - pState.lastInteractionTime;
        const decay = Math.exp(-t * 5);
        const vibration = Math.sin(t * 100) * 0.05 * decay * strength;
        posX += vibration;
        posY += vibration;
        posZ += vibration;
        if (decay < 0.01) pState.deformation = 0;
    }

    // Apply Transforms
    meshRef.current.position.set(posX, posY, posZ);
    meshRef.current.rotation.set(rotX, rotY, rotZ);
    meshRef.current.scale.set(scaleX, scaleY, scaleZ);
    
    // --- Material Animations (Uniform updates if possible) ---
    // Note: Direct material property updates in useFrame can be expensive if not careful.
    // For simple colors/emissive, it's okay.
    if (meshRef.current) {
        meshRef.current.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const m = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
                if (m) {
                    // Physics Material Effects
                    if (phys.includes('thermochromic-heating')) {
                        // Blend from base color to Red/Orange based on heat
                        const baseColor = new THREE.Color(obj.material.color);
                        const heatColor = new THREE.Color(1, 0.2, 0); // Red-hot
                        m.color.lerpColors(baseColor, heatColor, pState.heat);
                        m.emissive.lerpColors(new THREE.Color(0,0,0), new THREE.Color(1, 0.5, 0), pState.heat);
                        m.emissiveIntensity = pState.heat * 2;
                    }

                    if (phys.includes('rust-oxidation')) {
                         // Simulate rust by changing color to orange/brown over time if triggered
                         if (pState.rustLevel > 0) {
                             const baseColor = new THREE.Color(obj.material.color);
                             const rustColor = new THREE.Color('#8B4513');
                             m.color.lerpColors(baseColor, rustColor, pState.rustLevel);
                             m.roughness = THREE.MathUtils.lerp(obj.material.roughness, 1.0, pState.rustLevel);
                         }
                    }

                    if (phys.includes('condensation-wipe')) {
                        // High roughness = fog, Low roughness = clear
                        // Default condensationLevel starts at 1 (foggy) if we want, but let's say it builds up
                        // Actually logic above: interacting decreases condensation (clears it)
                        // So let's map condensationLevel to roughness
                        // 0 = clear (original roughness), 1 = foggy (roughness 0.8, metalness 0)
                        m.roughness = THREE.MathUtils.lerp(obj.material.roughness, 0.9, pState.condensationLevel);
                        m.metalness = THREE.MathUtils.lerp(obj.material.metalness, 0.1, pState.condensationLevel);
                    }

                    if (phys.includes('electrostatic-discharge') && pState.isInteracting) {
                        if (m.emissive) m.emissive.setHex(0x88ccff);
                        m.emissiveIntensity = 2 + Math.random() * 2 * strength;
                    } else if (phys.includes('electrostatic-discharge')) {
                         if (m.emissive) m.emissive.setHex(0x000000);
                         m.emissiveIntensity = 0;
                    }

                    // Rim Pulse / Chroma / Holo
                    if (anims.includes('rim')) {
                        m.emissiveIntensity = 1 + Math.sin(time * 3 * speed) * 1;
                        if (m.emissive && m.emissive.getHex() === 0) m.emissive.setHex(0x444444);
                    }
                    if (anims.includes('chroma')) {
                        m.color.setHSL((time * 0.1 * speed) % 1, 0.5, 0.5);
                    } else if (!phys.includes('thermochromic-heating') && !phys.includes('rust-oxidation')) {
                        // Revert to original color if no color-modifying physics/anim is active
                        const targetColor = new THREE.Color(obj.material.color);
                        if (!m.color.equals(targetColor)) {
                             m.color.copy(targetColor);
                        }
                    }
                    
                    if (anims.includes('dust') || phys.includes('dust-displacement')) {
                         m.transparent = true;
                         let opacity = 1;
                         if (anims.includes('dust')) opacity = 0.5 + Math.sin(time * 5 * speed) * 0.5;
                         if (phys.includes('dust-displacement') && pState.isInteracting) opacity = 0.7 + Math.random() * 0.3;
                         m.opacity = opacity;
                    }
                }
            }
        });
    }

  });

  const handlePointerDown = (e: { point: THREE.Vector3 }) => {
      // e.stopPropagation(); // Optional: might block transform controls
      physicsState.current.isInteracting = true;
      physicsState.current.lastInteractionTime = clock.getElapsedTime();
      physicsState.current.interactionPoint.copy(e.point);
      physicsState.current.dragStartPoint.copy(e.point);
      physicsState.current.deformation = 1.0; // Trigger jiggle/resonance

      if (obj.physics?.includes('rust-oxidation')) {
          physicsState.current.rustLevel = Math.min(physicsState.current.rustLevel + 0.2 * (obj.physicsStrength || 1), 1);
      }
      if (obj.physics?.includes('condensation-wipe')) {
          physicsState.current.condensationLevel = 1; // Start foggy
      }
      
      onSelect();
  };

  const handlePointerUp = () => {
      physicsState.current.isInteracting = false;
  };

  const handlePointerMove = (e: { point: THREE.Vector3 }) => {
      if (physicsState.current.isInteracting) {
          physicsState.current.interactionPoint.copy(e.point);
      }
  };

  const materialProps = {
    color: isAnnotatorActive && isHovered ? '#ffffff' : obj.material.color, // Highlight color when annotating
    roughness: obj.material.roughness,
    metalness: obj.material.metalness,
    emissive: (obj.material.emissiveIntensity > 0 || (isAnnotatorActive && isHovered)) ? (isAnnotatorActive && isHovered ? '#ffffff' : obj.material.color) : '#000000',
    emissiveIntensity: isAnnotatorActive && isHovered ? 0.5 : obj.material.emissiveIntensity,
    envMapIntensity: obj.material.ambientOcclusion,
    wireframe: obj.material.type.includes('wireframed'),
  };

  const commonEvents = {
      onPointerDown: (e: { stopPropagation: () => void; point: THREE.Vector3 }) => {
          if (isAnnotatorActive) {
              e.stopPropagation();
              onAnnotateSelect?.(obj);
              return;
          }
          handlePointerDown(e);
      },
      onPointerUp: handlePointerUp,
      onPointerOut: () => {
          setIsHovered(false);
          if (isAnnotatorActive) {
              document.body.style.cursor = 'auto';
              onHover?.(null);
          }
          handlePointerUp();
      },
      onPointerOver: (e: { stopPropagation: () => void }) => {
          setIsHovered(true);
          if (isAnnotatorActive) {
              e.stopPropagation();
              document.body.style.cursor = 'crosshair';
              onHover?.(obj);
          }
      },
      onPointerMove: handlePointerMove,
      onClick: (e: { stopPropagation: () => void }) => { 
          e.stopPropagation(); 
          if (isAnnotatorActive) {
              onAnnotateSelect?.(obj);
          } else {
              onSelect(); 
          }
      }
  };

  const renderGeometry = () => {
    switch (obj.type) {
      case 'cube': return <boxGeometry />;
      case 'sphere': return <sphereGeometry />;
      case 'cone': return <coneGeometry />;
      case 'cylinder': return <cylinderGeometry />;
      case 'tetrahedron': return <tetrahedronGeometry />;
      case 'octahedron': return <octahedronGeometry />;
      case 'dodecahedron': return <dodecahedronGeometry />;
      case 'icosahedron': return <icosahedronGeometry />;
      case 'capsule': return <capsuleGeometry />;
      case 'ring': return <ringGeometry />;
      case 'circle': return <circleGeometry />;
      case 'torus': return <torusKnotGeometry args={[0.6, 0.2, 128, 16]} />;
      case 'pyramid': return <coneGeometry args={[1, 1, 4]} />;
      case 'tube': return <cylinderGeometry args={[0.5, 0.5, 1, 32, 1, true]} />;
      case 'upload': return null; // Handled separately
      case 'custom': {
        if (!obj.points || obj.points.length < 3) return <boxGeometry />;
        const shape = new THREE.Shape();
        shape.moveTo(obj.points[0].x, obj.points[0].y);
        obj.points.forEach(p => shape.lineTo(p.x, p.y));
        return <extrudeGeometry args={[shape, { depth: 0.5, bevelEnabled: true, bevelSize: 0.05 }]} />;
      }
      case 'heart': {
        const heartShape = new THREE.Shape();
        const x = 0, y = 0;
        // Flip Y coordinates to make it right side up
        heartShape.moveTo( x + 0.5, y - 0.5 );
        heartShape.bezierCurveTo( x + 0.5, y - 0.5, x + 0.4, y, x, y );
        heartShape.bezierCurveTo( x - 0.6, y, x - 0.6, y - 0.7,x - 0.6, y - 0.7 );
        heartShape.bezierCurveTo( x - 0.6, y - 1.1, x - 0.3, y - 1.54, x + 0.5, y - 1.9 );
        heartShape.bezierCurveTo( x + 1.2, y - 1.54, x + 1.6, y - 1.1, x + 1.6, y - 0.7 );
        heartShape.bezierCurveTo( x + 1.6, y - 0.7, x + 1.6, y, x + 1.0, y );
        heartShape.bezierCurveTo( x + 0.7, y, x + 0.5, y - 0.5, x + 0.5, y - 0.5 );
        return <extrudeGeometry args={[heartShape, { depth: 0.4, bevelEnabled: true, bevelSize: 0.05 }]} />;
      }
      case 'star': {
        const starShape = new THREE.Shape();
        const outerRadius = 1;
        const innerRadius = 0.4;
        const spikes = 5;
        for (let i = 0; i < spikes * 2; i++) {
            const r = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i / (spikes * 2)) * Math.PI * 2;
            const sx = Math.cos(angle) * r;
            const sy = Math.sin(angle) * r;
            if (i === 0) starShape.moveTo(sx, sy);
            else starShape.lineTo(sx, sy);
        }
        starShape.closePath();
        return <extrudeGeometry args={[starShape, { depth: 0.2, bevelEnabled: true, bevelSize: 0.05 }]} />;
      }
      case 'metatron': 
        return <icosahedronGeometry args={[1, 1]} />;
      case 'text':
        return <boxGeometry args={[0,0,0]} />; // Handled separately
      default: return <boxGeometry />;
    }
  };

  const renderMaterial = () => {
    const hasEdges = obj.material.type.includes('edges');
    const isWireframe = obj.material.type.includes('wireframed');
    const outlines = hasEdges ? <Outlines thickness={0.05} color="white" /> : null;
    
    const finalMaterialProps = {
        ...materialProps,
        wireframe: isWireframe,
        side: THREE.DoubleSide
    };

    if (obj.material.texture) {
      return (
        <TextureErrorBoundary fallback={<><meshStandardMaterial {...finalMaterialProps} />{outlines}</>}>
            <Suspense fallback={<><meshStandardMaterial {...finalMaterialProps} />{outlines}</>}>
                <TexturedMaterial url={obj.material.texture} materialProps={finalMaterialProps} type={obj.material.type} />
                {outlines}
            </Suspense>
        </TextureErrorBoundary>
      );
    }

    if (obj.material.type.includes('distort')) return <><MeshDistortMaterial {...materialProps} wireframe={isWireframe} speed={2} distort={0.4} />{outlines}</>;
    if (obj.material.type.includes('wobble')) return <><MeshWobbleMaterial {...materialProps} wireframe={isWireframe} factor={1} speed={2} />{outlines}</>;
    
    return <><meshStandardMaterial {...finalMaterialProps} />{outlines}</>;
  };


  const handleTransformEnd = () => {
      isDragging.current = false;
      if (meshRef.current) {
          onUpdate({
              position: meshRef.current.position.toArray(),
              rotation: [meshRef.current.rotation.x, meshRef.current.rotation.y, meshRef.current.rotation.z],
              scale: meshRef.current.scale.toArray()
          });
      }
  };

  const handleTransformStart = () => {
      isDragging.current = true;
  };

  if (obj.type === 'upload' && obj.modelUrl) {
      return (
          <>
            <group 
                ref={meshRef}
                position={obj.position} 
                rotation={obj.rotation} 
                scale={obj.scale} 
                {...commonEvents}
            >
                <Suspense fallback={<mesh><boxGeometry /><meshStandardMaterial wireframe /></mesh>}>
                    <ModelErrorBoundary fallback={
                        <group>
                            <mesh>
                                <boxGeometry args={[1, 1, 1]} />
                                <meshStandardMaterial color="red" wireframe />
                            </mesh>
                            <Html position={[0, 1.2, 0]} center>
                                <div className="bg-red-500/90 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
                                    Model Load Error (Expired)
                                </div>
                            </Html>
                        </group>
                    }>
                        <UploadedModel 
                            url={obj.modelUrl} 
                            modelType={obj.modelType}
                            materialProps={materialProps} 
                            subMaterials={obj.subMaterials}
                            useOriginalMaterial={obj.useOriginalMaterial}
                        />
                    </ModelErrorBoundary>
                </Suspense>
            </group>
            {/* eslint-disable-next-line react-hooks/refs */}
            {isSelected && showTransformControls && meshRef.current && (
                <TransformControls 
                    // eslint-disable-next-line react-hooks/refs
                    object={meshRef.current} 
                    mode={transformMode} 
                    showX={showTransformControls}
                    showY={showTransformControls}
                    showZ={showTransformControls}
                    onMouseDown={handleTransformStart}
                    onMouseUp={handleTransformEnd}
                />
            )}
          </>
      );
  }

  if (obj.type === 'text') {
      return (
        <>
            <group 
                ref={meshRef}
                position={obj.position} 
                rotation={obj.rotation} 
                scale={obj.scale} 
                {...commonEvents}
            >
                <Center>
                    <FontErrorBoundary fallback={
                        <Text3D font={FONT_OPTIONS[0].value} size={0.5} height={0.2} curveSegments={12} bevelEnabled bevelThickness={0.02} bevelSize={0.02} bevelOffset={0} bevelSegments={5} castShadow receiveShadow>
                             {obj.isSmallCaps ? toSmallCaps(obj.text || 'TEXT') : (obj.text || 'TEXT')}
                             {renderMaterial()}
                        </Text3D>
                    }>
                        <Text3D font={obj.font || FONT_OPTIONS[0].value} size={0.5} height={0.2} curveSegments={12} bevelEnabled bevelThickness={0.02} bevelSize={0.02} bevelOffset={0} bevelSegments={5} castShadow receiveShadow>
                            {obj.isSmallCaps ? toSmallCaps(obj.text || 'TEXT') : (obj.text || 'TEXT')}
                            {renderMaterial()}
                        </Text3D>
                    </FontErrorBoundary>
                </Center>
            </group>
            {/* eslint-disable-next-line react-hooks/refs */}
            {isSelected && showTransformControls && meshRef.current && (
                <TransformControls 
                    // eslint-disable-next-line react-hooks/refs
                    object={meshRef.current} 
                    mode={transformMode} 
                    showX={showTransformControls}
                    showY={showTransformControls}
                    showZ={showTransformControls}
                    onMouseDown={handleTransformStart}
                    onMouseUp={handleTransformEnd}
                />
            )}
        </>
      );
  }

  if (obj.type === 'metatron') {
      return (
          <>
            <group 
                ref={meshRef}
                position={obj.position} 
                rotation={obj.rotation} 
                scale={obj.scale} 
                {...commonEvents}
            >
                <Float speed={2} rotationIntensity={1} floatIntensity={1}>
                    {/* Central Sphere */}
                    <mesh castShadow receiveShadow>
                        <icosahedronGeometry args={[0.5, 0]} />
                        {renderMaterial()}
                    </mesh>
                    {/* Surrounding Spheres */}
                    {[...Array(12)].map((_, i) => {
                        const radius = 1;
                        // Distribute on a sphere surface roughly
                        const phi = Math.acos( -1 + ( 2 * i ) / 12 );
                        const theta = Math.sqrt( 12 * Math.PI ) * phi;
                        const x = radius * Math.cos( theta ) * Math.sin( phi );
                        const y = radius * Math.sin( theta ) * Math.sin( phi );
                        const z = radius * Math.cos( phi );
                        return (
                            <mesh key={i} position={[x, y, z]} castShadow receiveShadow>
                                <sphereGeometry args={[0.2, 16, 16]} />
                                {renderMaterial()}
                            </mesh>
                        )
                    })}
                    {/* Connecting Lines (Metatron's Cube edges - simplified) */}
                    <mesh castShadow receiveShadow>
                        <icosahedronGeometry args={[1, 0]} />
                        <meshStandardMaterial {...materialProps} wireframe />
                    </mesh>
                </Float>
            </group>
            {/* eslint-disable-next-line react-hooks/refs */}
            {isSelected && showTransformControls && meshRef.current && (
                <TransformControls 
                    // eslint-disable-next-line react-hooks/refs
                    object={meshRef.current} 
                    mode={transformMode} 
                    showX={showTransformControls}
                    showY={showTransformControls}
                    showZ={showTransformControls}
                    onMouseDown={handleTransformStart}
                    onMouseUp={handleTransformEnd}
                />
            )}
          </>
      )
  }

  return (
    <group>
      <mesh
        ref={meshRef as React.RefObject<THREE.Mesh>}
        position={obj.position}
        rotation={obj.rotation}
        scale={obj.scale}
        castShadow
        receiveShadow
        {...commonEvents}
      >
        {renderGeometry()}
        {renderMaterial()}
      </mesh>
      {/* eslint-disable-next-line react-hooks/refs */}
      {isSelected && showTransformControls && meshRef.current && (
        <TransformControls 
            // eslint-disable-next-line react-hooks/refs
            object={meshRef.current} 
            mode={transformMode} 
            showX={showTransformControls}
            showY={showTransformControls}
            showZ={showTransformControls}
            onMouseDown={handleTransformStart}
            onMouseUp={handleTransformEnd}
        />
      )}
    </group>
  );
};

const DrawingCanvas = ({ isDrawing, onDrawComplete }: { isDrawing: boolean, onDrawComplete: (points: THREE.Vector2[]) => void }) => {
  const [points, setPoints] = useState<THREE.Vector2[]>([]);
  const drawingRef = useRef(false);

  const handlePointerDown = (e: { stopPropagation: () => void; point: THREE.Vector3 }) => {
    if (!isDrawing) return;
    e.stopPropagation(); // Prevent orbit controls from taking over
    drawingRef.current = true;
    // Use the intersection point directly from the event
    const point = new THREE.Vector2(e.point.x, e.point.y);
    setPoints([point]);
  };

  const handlePointerMove = (e: { stopPropagation: () => void; point: THREE.Vector3 }) => {
    if (!isDrawing || !drawingRef.current) return;
    e.stopPropagation();
    const point = new THREE.Vector2(e.point.x, e.point.y);
    setPoints(prev => [...prev, point]);
  };

  const handlePointerUp = (e: { stopPropagation: () => void }) => {
    if (!isDrawing || !drawingRef.current) return;
    e.stopPropagation();
    drawingRef.current = false;
    if (points.length > 2) {
        onDrawComplete(points);
    }
    setPoints([]);
  };

  if (!isDrawing) return null;

  return (
    <mesh 
        onPointerDown={handlePointerDown} 
        onPointerMove={handlePointerMove} 
        onPointerUp={handlePointerUp}
        position={[0, 0, 0.1]} // Slightly in front
    >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
        {points.length > 1 && (
             <Line points={points.map(p => [p.x, p.y, 0])} color="red" lineWidth={3} />
        )}
    </mesh>
  );
};

const Notification = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => (
  <div className={cn(
    "absolute bottom-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-xs font-bold animate-in slide-in-from-bottom-5 fade-in duration-300",
    type === 'success' ? "bg-[rgba(0,0,0,0.98)] text-white" : "bg-red-500 text-white"
  )}>
    {type === 'success' ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
    {message}
    <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
  </div>
);

interface Creator3DProps {
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  todoPosition: TaskPosition;
  setTodoPosition: React.Dispatch<React.SetStateAction<TaskPosition>>;
  hue?: number;
  byokKeys?: Record<string, string>;
  matildaModel?: string;
  isTodoMinimized?: boolean;
  setIsTodoMinimized?: (minimized: boolean) => void;
  fitContainer?: boolean;
}

import { useCreatorStore } from '../../store/useCreatorStore';

export function Creator3D({ tasks, setTasks, todoPosition, setTodoPosition, hue, byokKeys, matildaModel, isTodoMinimized, setIsTodoMinimized, fitContainer }: Creator3DProps) {
  const { user } = useAuth();
  const userTier = user?.tier || 'guest';
  const isInnovator = userTier === 'innovator' || userTier === 'admin';
  const { isRecording: isGlobalRecording } = useRecording();
  const objects = useCreatorStore(s => s.objects);
  const setObjects = useCreatorStore(s => s.setObjects);
  const [selectedId, setSelectedId] = useState<string>('1');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const [background, setBackground] = useState<BackgroundType>('none');
  const [backgroundSpeed, setBackgroundSpeed] = useState(1);
  const [backgroundLightColor, setBackgroundLightColor] = useState('#ffffff');
  const [backgroundDarkColor, setBackgroundDarkColor] = useState('#000000');
  
  // Vanta Specific State
  const [vantaConfig, setVantaConfig] = useState({
      backgroundColor: '#183550',
      lightColor: '#ff9900',
      skyColor: '#68b8d7',
      cloudColor: '#adc1de',
      cloudShadowColor: '#183550',
      sunColor: '#ff9919',
      sunGlareColor: '#ff6633',
      sunlightColor: '#ff9919',
      maxDistance: 29,
      spacing: 15,
      showDots: false,
      points: 20
  });
  const [lighting, setLighting] = useState<LightingType>('studio');
  const [chatInput, setChatInput] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [activeCodeFile, setActiveCodeFile] = useState<'js' | 'html' | 'css'>('js');
  const [codeContent, setCodeContent] = useState('');
  const [userEdits, setUserEdits] = useState<{[key: string]: string}>({});
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const orbitControlsRef = useRef<Record<string, unknown> | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showAxis, setShowAxis] = useState(true);
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate' | 'scale'>('translate');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isToolsMinimized, setIsToolsMinimized] = useState(true);
  const [isAiBarMinimized, setIsAiBarMinimized] = useState(false);
  const [isLeftPanelMinimized, setIsLeftPanelMinimized] = useState(false);
  const [showImagineModal, setShowImagineModal] = useState(false);
  const [imaginePrompt, setImaginePrompt] = useState('');
  
  // Annotator State
  const [isAnnotatorActive, setIsAnnotatorActive] = useState(false);
  const [annotatedObject, setAnnotatedObject] = useState<SceneObject | null>(null);
  const [hoveredObject, setHoveredObject] = useState<SceneObject | null>(null);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>(matildaModel || 'gemini-3.1-flash-lite-preview');

  useEffect(() => {
    if (matildaModel) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedModel(matildaModel);
    }
  }, [matildaModel]);

  const handleAnnotate = (text: string, objectInfo: SceneObject) => {
    const newTask: Task = {
      id: uuidv4(),
      text,
      completed: false,
      x: 50, // Center or calculate screen pos if possible
      y: 50,
      timestamp: Date.now(),
      elementInfo: JSON.stringify(objectInfo),
      assignee: 'Me',
      position3D: objectInfo.position
    };
    setTasks(prev => [...prev, newTask]);
    setIsAnnotatorActive(false); // Close annotator after adding
  };

  const handleAskAI = async (objectInfo: SceneObject, notes: string) => {
    if (!isInnovator) {
      alert("AI Analysis is a Pro feature. Please upgrade to access.");
      return;
    }
    if (isGenerating) return;
    setIsGenerating(true);
    setAiMessage("Thinking...");
    
    const prompt = `
    I have a 3D object in my scene that I've annotated.
    
    Object Info:
    ${JSON.stringify(objectInfo, null, 2)}
    
    My Notes:
    ${notes}
    
    Please explain what this object is and how my notes relate to it. Be concise, educational, informative, and easy to understand.
    `;

    try {
      const responseText = await generateText(prompt, 'gemini-3.1-flash-lite-preview');
      setAiMessage(responseText || "No response from AI.");
    } catch (error) {
      console.error("Error asking AI:", error);
      setAiMessage("Failed to analyze object.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendToChat = (message: string) => {
      setChatInput(message);
      // Ideally we would trigger handleAIChat here, but it depends on chatInput state which is async.
      // We can modify handleAIChat to accept an argument or just set the input and let user press enter/send.
      // Or we can call handleAIChat with the message directly if we refactor it.
      // For now, let's just set the input and maybe focus it.
      // Better: Call a version of handleAIChat that takes a message.
      handleAIChat(message);
  };
  
  // Undo/Redo Stacks
  const [undoStack, setUndoStack] = useState<SceneObject[][]>([]);
  const [, setRedoStack] = useState<SceneObject[][]>([]);

  const [isGenerating, setIsGenerating] = useState(false);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const compositeFrameRef = useRef<number>(0);
  
  const sceneRef = useRef<THREE.Group>(null);
  // We need a ref to the canvas container to capture stream if possible, 
  // but standard way is to get canvas from useThree inside. 
  // We'll use a hack to get the canvas element via a class selector or ref if possible.
  // Since Canvas creates a canvas element, we can find it.

  const selectedObject = objects.find(o => o.id === selectedId) || objects[0];

  const addToHistory = (newObjects: SceneObject[]) => {
    setUndoStack(prev => [...prev, objects]);
    setRedoStack([]);
    setObjects(newObjects);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, objects]);
    setUndoStack(prev => prev.slice(0, -1));
    setObjects(previous);
  };

  const updateSelectedObject = (updates: Partial<SceneObject> | Partial<SceneObject['material']>) => {
    const newObjects = objects.map(obj => {
      if (obj.id === selectedId) {
        // Determine if we are updating material or the object itself
        // We check for specific material properties
        let isMaterialUpdate = false;
        if ('type' in updates) {
             // If type is an array, it's a material type update (MaterialType[])
             if (Array.isArray(updates.type)) {
                 isMaterialUpdate = true;
             }
             // If type is a string, it's a shape type update (ShapeType), so isMaterialUpdate is false.
        } else {
             const materialKeys = ['color', 'roughness', 'metalness', 'emissive', 'emissiveIntensity', 'ambientOcclusion', 'texture'];
             isMaterialUpdate = materialKeys.some(k => k in updates);
        }
        
        let updatedObj = { ...obj };

        if (isMaterialUpdate) {
           // If user manually updates material, disable "useOriginalMaterial"
           updatedObj.useOriginalMaterial = false;
           // We need to cast updates to any to satisfy TS here because of the intersection type ambiguity
           updatedObj = { ...updatedObj, material: { ...updatedObj.material, ...(updates as Record<string, unknown>) } };
        } else {
           updatedObj = { ...updatedObj, ...(updates as Record<string, unknown>) };
        }
        return updatedObj;
      }
      return obj;
    });
    addToHistory(newObjects);
  };

  const addObject = (type: ShapeType = 'cube', points?: THREE.Vector2[], url?: string, modelType?: 'gltf' | 'obj') => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newObj: SceneObject = {
      id: newId,
      type: type,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      material: {
        type: ['standard'],
        color: type === 'upload' ? '#ffffff' : '#' + Math.floor(Math.random()*16777215).toString(16),
        roughness: 0.5,
        metalness: 0.5,
        emissive: '#000000',
        emissiveIntensity: 0,
        ambientOcclusion: 1,
        // Texture field is now separate from model URL
      },
      animation: [],
      animationSpeed: 1,
      points: points,
      text: type === 'text' ? 'TEXT' : undefined,
      font: type === 'text' ? FONT_OPTIONS[0].value : undefined,
      isSmallCaps: type === 'text' ? false : undefined,
      modelUrl: type === 'upload' ? url : undefined,
      modelType: modelType,
      useOriginalMaterial: type === 'upload' // Default to true for uploads
    };
    addToHistory([...objects, newObj]);
    setSelectedId(newId);
  };

  const removeObject = (id: string) => {
    if (objects.length <= 1) return;
    const newObjects = objects.filter(o => o.id !== id);
    addToHistory(newObjects);
    if (selectedId === id) {
        setSelectedId(newObjects[0].id);
    }
  };

  const handleDrawComplete = (points: THREE.Vector2[]) => {
      const box = new THREE.Box2().setFromPoints(points);
      const center = new THREE.Vector2();
      box.getCenter(center);
      const centeredPoints = points.map(p => p.clone().sub(center));
      addObject('custom', centeredPoints);
      
      // Update position after adding (requires another state update, but we can just do it in one go if we refactor addObject, 
      // but for now let's just leave it at 0,0,0 or update it)
      // Actually addObject adds to history, so we should be careful. 
      // Simplified: Just add at 0,0,0.
      setIsDrawing(false);
  };

  const [hasLoaded, setHasLoaded] = useState(false);

  // Persistence
  useEffect(() => {
    const load = async () => {
      const saved = await getScene('latest');
      if (saved && saved.objects && saved.objects.length > 0) {
        // Sanitize objects to remove stale blob URLs which cause crashes
        const sanitizedObjects = saved.objects.map((obj: SceneObject) => {
            if (obj.modelUrl && obj.modelUrl.startsWith('blob:')) {
                console.warn("Removed stale blob URL for object", obj.id);
                return { ...obj, modelUrl: undefined, type: 'cube' as ShapeType };
            }
            return obj;
        });
        setObjects(sanitizedObjects);
        setBackground(saved.background as BackgroundType);
        setLighting(saved.lighting as LightingType);
        if (saved.backgroundSpeed !== undefined) setBackgroundSpeed(saved.backgroundSpeed);
        if (saved.backgroundLightColor) setBackgroundLightColor(saved.backgroundLightColor);
        if (saved.backgroundDarkColor) setBackgroundDarkColor(saved.backgroundDarkColor);
        if (saved.vantaConfig) setVantaConfig(saved.vantaConfig);
        if (saved.codeContent) setCodeContent(saved.codeContent);
      } else {
        // Initialize with default object if empty
        setObjects([{
          id: uuidv4(),
          type: 'torus',
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          material: {
            type: ['standard'],
            color: '#ff69b4',
            roughness: 0.5,
            metalness: 0.5,
            emissive: '#000000',
            emissiveIntensity: 0,
            ambientOcclusion: 1
          },
          animation: [],
          animationSpeed: 1
        }]);
      }
      setHasLoaded(true);
    };
    load();
  }, []);

  useEffect(() => {
    if (hasLoaded) {
      saveScene({
        id: 'latest',
        objects,
        background,
        lighting,
        backgroundSpeed,
        backgroundLightColor,
        backgroundDarkColor,
        vantaConfig,
        codeContent
      });
    }
  }, [hasLoaded, objects, background, lighting, backgroundSpeed, backgroundLightColor, backgroundDarkColor, vantaConfig, codeContent]);

    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [showApiKeyHelp, setShowApiKeyHelp] = useState(false);
    const selectorRef = useRef<HTMLDivElement>(null);
    const [showBYOKModal, setShowBYOKModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalModelName, setAuthModalModelName] = useState('');
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadModelId, setDownloadModelId] = useState<OfflineModelId | null>(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [pendingModel, setPendingModel] = useState<OnlineModelType | OfflineModelId | null>(null);
    const [byokKey, setByokKey] = useState<string | undefined>(undefined);

    const handleModelSelect = async (modelId: OnlineModelType | OfflineModelId) => {
        const isOffline = OFFLINE_MODELS.some(m => m.id === modelId);
        const onlineModel = ONLINE_MODELS.find(m => m.id === modelId);
        const offlineModel = OFFLINE_MODELS.find(m => m.id === modelId);
        const model = onlineModel || offlineModel;

        const userTier = user?.tier || 'unauthenticated';

        if (model && !model.tiers.includes(userTier)) {
            if (userTier === 'unauthenticated') {
                setAuthModalModelName(model.name);
                setShowAuthModal(true);
            } else {
                alert(`${model.name} is restricted to ${model.tiers.join('/')} tiers.`);
            }
            return;
        }

        if (isOffline) {
            const downloaded = await isModelDownloaded(modelId as OfflineModelId);
            if (!downloaded) {
                setPendingModel(modelId);
                setDownloadModelId(modelId as OfflineModelId);
                setShowDownloadModal(true);
                setIsSelectorOpen(false);
                return;
            }
            setSelectedModel(modelId as string);
            setIsSelectorOpen(false);
            return;
        }

        // Unauthenticated users MUST use BYOK for online models
        if (userTier === 'unauthenticated') {
            setPendingModel(modelId);
            setShowBYOKModal(true);
            setIsSelectorOpen(false);
            return;
        }

        setSelectedModel(modelId as string);
        setIsSelectorOpen(false);
    };

    const handleDownloadComplete = () => {
        setShowDownloadModal(false);
        if (pendingModel) {
            setSelectedModel(pendingModel as string);
            setPendingModel(null);
        }
    };

    const handleBYOKConfirm = (useBYOK: boolean, key?: string) => {
        if (pendingModel) {
            setSelectedModel(pendingModel as string);
            if (useBYOK && key) {
                setByokKey(key);
            }
        }
        setShowBYOKModal(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                setIsSelectorOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

  const handleAIChat = async (messageOverride?: string) => {
    // Determine effective key
    const effectiveKey = byokKey || (byokKeys && byokKeys[selectedModel]);
    const isGemini = selectedModel.startsWith('gemini');
    const userTier = user?.tier || 'unauthenticated';
    
    // 1. Unauthenticated users must have key for EVERYTHING (including Gemini)
    if (userTier === 'unauthenticated' && !effectiveKey) {
       // DEMO MODE
       setIsGenerating(true);
       await new Promise(resolve => setTimeout(resolve, 1000));
       const responseText = getRandomDemoResponse('chat');
       setAiMessage(responseText);
       setIsGenerating(false);
       return;
    }
    
    // 2. All models except Gemini Flash 3, Pro 3 and Demo require sign-in
    if (userTier === 'unauthenticated' && selectedModel !== 'gemini-3.1-flash-lite-preview' && selectedModel !== 'gemini-3.1-pro-preview') {
        const model = ONLINE_MODELS.find(m => m.id === selectedModel) || OFFLINE_MODELS.find(m => m.id === selectedModel);
        setAuthModalModelName(model?.name || selectedModel);
        setShowAuthModal(true);
        return;
    }
    
    const isOffline = selectedModel === 'gemma-3n-e4b-it';
    if (!isGemini && !effectiveKey && !isOffline) {
       setAiMessage(`Please provide a BYOK key for ${selectedModel}.`);
       return;
    }

    const message = messageOverride || chatInput;
    if (!message.trim()) return;
      setIsGenerating(true);
      setAiMessage(null);
      
      try {
        // Determine API Key to pass to service
        const apiKeyToUse = effectiveKey;

        if (!apiKeyToUse && !isOffline) {
            setAiMessage(`Please provide a BYOK API key for ${selectedModel}.`);
            setIsGenerating(false);
            return;
        }

        // Construct scene context
        const sceneContext = JSON.stringify(objects.map(o => ({
            id: o.id,
            type: o.type,
            position: o.position,
            color: o.material.color
        })));

        const actions = await parsePromptTo3DAction(message, sceneContext, apiKeyToUse, selectedModel);
        
        if (actions.length > 0) {
            const newObjects = [...objects];
            
            for (const action of actions) {
                if (action.type === 'add') {
                    const props = action.properties;
                    const newObj: SceneObject = {
                        id: uuidv4(),
                        type: (props.type as ShapeType) || 'cube',
                        position: (props.position as [number, number, number]) || [0, 0, 0],
                        rotation: (props.rotation as [number, number, number]) || [0, 0, 0],
                        scale: (props.scale as [number, number, number]) || [1, 1, 1],
                        material: {
                            type: ['standard'],
                            color: ((props.material as Record<string, unknown>)?.color as string) || '#ffffff',
                            roughness: 0.5,
                            metalness: 0.5,
                            emissive: '#000000',
                            emissiveIntensity: 0,
                            ambientOcclusion: 1
                        },
                        animation: 'none',
                        animationSpeed: 1
                    };
                    newObjects.push(newObj);
                } else if (action.type === 'update') {
                    const targetId = action.target;
                    const targetIndex = newObjects.findIndex(o => o.id === targetId || (action.target === 'all'));
                    if (targetIndex !== -1) {
                         const obj = newObjects[targetIndex];
                         if (action.properties.position) obj.position = action.properties.position as [number, number, number];
                         if (action.properties.color) obj.material.color = action.properties.color as string;
                    }
                } else if (action.type === 'background') {
                    if (action.properties.type) {
                        setBackgroundType(action.properties.type as BackgroundType);
                    }
                }
            }
            
            setObjects(newObjects);
            addToHistory(newObjects);
            setAiMessage(`Executed ${actions.length} actions.`);
        } else {
            setAiMessage("I couldn't understand how to apply that to the scene. Try 'Add a red cube' or 'Move the sphere up'.");
        }

    } catch (error) {
        console.error("AI Chat Error:", error);
        setAiMessage("Error processing request.");
    } finally {
        setIsGenerating(false);
        setChatInput('');
    }
  };

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const handleSaveScene = () => {
      setSaveName(`Scene ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`);
      setIsSaveModalOpen(true);
  };

  const confirmSave = async (location: 'cache' | 'cloud') => {
      setIsSaveModalOpen(false);
      
      try {
          // Check for existing asset with same name
          const existing = await checkAssetExists(saveName, 'scene');
          let idToUse: string | undefined = undefined;

          if (existing) {
              if (!confirm(`A scene named "${saveName}" already exists. Do you want to overwrite it?`)) {
                  return; // Cancel save
              }
              idToUse = existing.id;
          }

          // Capture thumbnail
          let thumbnailUrl = '';
          // Find the main 3D canvas (usually the second one if Starfield is first, or check context)
          const canvases = document.querySelectorAll('canvas');
          let targetCanvas = canvases[0];
          canvases.forEach(c => {
              // Heuristic: The 3D canvas usually has a 'webgl' context and is larger/main
              // Or we can rely on the fact that Starfield might be z-index 0
              if (c.getAttribute('data-engine') === 'three.js r134') { // Vanta adds this sometimes
                  // Vanta canvas
              } else {
                  // Likely our main canvas
                  targetCanvas = c;
              }
          });
          
          // Better: use the one inside the containerRef if possible, but we are outside
          // Just try the last one as it's usually the overlay or the main one
          if (canvases.length > 0) {
             // Iterate to find the one with webgl context that isn't vanta
             for (let i = 0; i < canvases.length; i++) {
                 const gl = canvases[i].getContext('webgl') || canvases[i].getContext('webgl2');
                 if (gl) {
                     targetCanvas = canvases[i];
                     // Break if we think this is the main one. 
                     // If we have Vanta, it also uses WebGL. 
                     // The main canvas is usually the one with the scene.
                     // Let's assume the last WebGL canvas is the main one (React Three Fiber usually mounts last)
                 }
             }
          }

          if (targetCanvas) {
              thumbnailUrl = targetCanvas.toDataURL('image/png', 0.8);
          }

          const sceneData = {
              objects,
              background,
              lighting,
              aspectRatio
          };

          await saveAsset({
              id: idToUse,
              type: 'scene', // Use 'scene' type for 3D scenes
              name: saveName,
              url: thumbnailUrl, // Use thumbnail as the main URL for display
              metadata: sceneData
          });
          
          if (location === 'cloud') {
              alert(`Scene "${saveName}" saved to Cloud (Simulated)!`);
          } else {
              alert(`Scene "${saveName}" saved to Cache!`);
          }
      } catch (e) {
          console.error("Failed to save scene", e);
          alert("Failed to save scene.");
      }
  };

  const handleExportGLTF = () => {
    if (!sceneRef.current) {
        alert("Scene not ready for export. Please add an object first.");
        return;
    }
    
    try {
        const exporter = new GLTFExporter();
        exporter.parse(
          sceneRef.current,
          (gltf) => {
            const output = JSON.stringify(gltf, null, 2);
            const blob = new Blob([output], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `paradox_scene_${Date.now()}.gltf`;
            link.click();
            URL.revokeObjectURL(url);
          },
          (error) => {
              console.error("GLTF Export Error:", error);
              alert("GLTF Export failed. See console for details.");
          },
          { binary: false }
        );
    } catch (err) {
        console.error("GLTF Exporter Error:", err);
        alert("GLTF Exporter failed to initialize.");
    }
  };

  const handleExportOBJ = () => {
    if (!sceneRef.current) {
        alert("Scene not ready for export.");
        return;
    }
    
    try {
        const exporter = new OBJExporter();
        const result = exporter.parse(sceneRef.current);
        const blob = new Blob([result], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `paradox_scene_${Date.now()}.obj`;
        link.click();
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("OBJ Export Error:", err);
        alert("OBJ Export failed.");
    }
  };


  const handleExportEnv = () => {
      const env = {
          objects,
          background,
          lighting,
          aspectRatio
      };
      const blob = new Blob([JSON.stringify(env, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'environment.json';
      link.click();
  };

  const handleSnapshot = () => {
      const canvas = document.querySelector('canvas');
      if (canvas) {
          // To ensure highest resolution, we can check if the device supports high DPI
          // R3F already handles pixelRatio, but we can force a high-quality capture.
          const link = document.createElement('a');
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          link.download = `paradox_highres_${timestamp}.png`;
          
          // Capture with maximum quality
          const dataUrl = canvas.toDataURL('image/png', 1.0);
          link.href = dataUrl;
          link.click();
      }
  };

  const handleNewScene = () => {
        // Force a complete state reset
        const defaultObject: SceneObject = {
            id: '1',
            type: 'torus',
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
            material: {
              type: ['standard'],
              color: '#ff69b4',
              roughness: 0.5,
              metalness: 0.5,
              emissive: '#000000',
              emissiveIntensity: 0,
              ambientOcclusion: 1,
            },
            animation: [],
            animationSpeed: 1,
        };
        
        setObjects([defaultObject]);
        setSelectedId('1');
        setUndoStack([]);
        setRedoStack([]);
        setBackground('none');
        setLighting('studio');
        setAspectRatio('16:9');
        setSaveName('');
  };

  const handleRecord = () => {
      if (isRecording) {
          // Stop recording
          mediaRecorderRef.current?.stop();
          setIsRecording(false);
          if (compositeFrameRef.current) cancelAnimationFrame(compositeFrameRef.current);
          return;
      }

      const container = containerRef.current;
      if (!container) return;

      // Find all canvases (Vanta + Three.js)
      const canvases = Array.from(container.querySelectorAll('canvas'));
      if (canvases.length === 0) return;

      let stream: MediaStream;

      if (canvases.length > 1) {
          // Composite multiple canvases (e.g. Vanta background + Three.js foreground)
          const width = canvases[0].width;
          const height = canvases[0].height;
          
          const compositeCanvas = document.createElement('canvas');
          compositeCanvas.width = width;
          compositeCanvas.height = height;
          const ctx = compositeCanvas.getContext('2d');
          
          if (!ctx) return;

          // Create a MediaStreamDestination to capture the composite
          stream = compositeCanvas.captureStream(60);

          const draw = () => {
              if (mediaRecorderRef.current?.state !== 'recording') return;
              
              ctx.clearRect(0, 0, width, height);
              canvases.forEach(c => {
                  ctx.drawImage(c, 0, 0, width, height);
              });
              
              // Use setTimeout to throttle if needed, but requestAnimationFrame is standard.
              // Ensure we don't stack up frames if something is slow.
              compositeFrameRef.current = requestAnimationFrame(draw);
          };

          // Start the loop after recorder starts
          setTimeout(draw, 100); 
      } else {
          // Single canvas (e.g. just Three.js with internal background)
          stream = canvases[0].captureStream(60);
      }
          
      // Prefer MP4/H.264 if available, then VP9, then default
      let mimeType = 'video/webm';
      if (MediaRecorder.isTypeSupported('video/mp4; codecs=avc1.42E01E, mp4a.40.2')) {
          mimeType = 'video/mp4; codecs=avc1.42E01E, mp4a.40.2';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
          mimeType = 'video/mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
          mimeType = 'video/webm; codecs=vp9';
      }

      const mediaRecorder = new MediaRecorder(stream, { 
          mimeType,
          videoBitsPerSecond: 25000000 // 25 Mbps for 4K quality
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
              chunksRef.current.push(e.data);
          }
      };

      mediaRecorder.onstop = () => {
          if (compositeFrameRef.current) cancelAnimationFrame(compositeFrameRef.current);
          
          // Determine extension based on mimeType
          const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
          const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `recording-${Date.now()}.${ext}`;
          link.click();
      };

      mediaRecorder.start();
      setIsRecording(true);
  };

  const generateCode = (fonts: Record<string, unknown> = {}) => {
    const fontsJson = JSON.stringify(fonts);
    const objectsCode = objects.map(obj => {
      // eslint-disable-next-line no-useless-assignment
      let geometryCode = '';
      switch(obj.type) {
        case 'cube': geometryCode = `new THREE.BoxGeometry(${obj.scale.map(s => s * 1).join(', ')})`; break;
        case 'sphere': geometryCode = `new THREE.SphereGeometry(${obj.scale[0]}, 32, 32)`; break;
        case 'torus': geometryCode = `new THREE.TorusKnotGeometry(${obj.scale[0]}, ${obj.scale[0]/3}, 128, 16)`; break;
        case 'cylinder': geometryCode = `new THREE.CylinderGeometry(${obj.scale[0]}, ${obj.scale[0]}, ${obj.scale[1]}, 32)`; break;
        case 'cone': geometryCode = `new THREE.ConeGeometry(${obj.scale[0]}, ${obj.scale[1]}, 32)`; break;
        case 'tetrahedron': geometryCode = `new THREE.TetrahedronGeometry(${obj.scale[0]})`; break;
        case 'octahedron': geometryCode = `new THREE.OctahedronGeometry(${obj.scale[0]})`; break;
        case 'dodecahedron': geometryCode = `new THREE.DodecahedronGeometry(${obj.scale[0]})`; break;
        case 'icosahedron': geometryCode = `new THREE.IcosahedronGeometry(${obj.scale[0]})`; break;
        case 'capsule': geometryCode = `new THREE.CapsuleGeometry(${obj.scale[0]}, ${obj.scale[1]}, 4, 8)`; break;
        case 'ring': geometryCode = `new THREE.RingGeometry(${obj.scale[0] * 0.5}, ${obj.scale[0]}, 32)`; break;
        case 'circle': geometryCode = `new THREE.CircleGeometry(${obj.scale[0]}, 32)`; break;
        case 'pyramid': geometryCode = `new THREE.ConeGeometry(${obj.scale[0]}, ${obj.scale[1]}, 4)`; break;
        case 'tube': geometryCode = `new THREE.CylinderGeometry(${obj.scale[0] * 0.5}, ${obj.scale[0] * 0.5}, ${obj.scale[1]}, 32, 1, true)`; break;
        case 'metatron': geometryCode = `new THREE.IcosahedronGeometry(${obj.scale[0]}, 0)`; break;
        case 'text': geometryCode = `new THREE.BoxGeometry(1,1,1)`; break; // Placeholder, handled below
        default: geometryCode = `new THREE.BoxGeometry(1, 1, 1)`;
      }

      const animations = Array.isArray(obj.animation) ? obj.animation : [obj.animation];
      const phys = obj.physics || [];
      const strength = obj.physicsStrength || 1;

      // Special handling for complex shapes
      if (obj.type === 'metatron') {
          return {
              id: obj.id.replace(/-/g, '_'),
              init: `
              // Metatron's Cube (${obj.id})
              const group_${obj.id.replace(/-/g, '_')} = new THREE.Group();
              group_${obj.id.replace(/-/g, '_')}.position.set(${obj.position.join(', ')});
              group_${obj.id.replace(/-/g, '_')}.rotation.set(${obj.rotation.join(', ')});
              group_${obj.id.replace(/-/g, '_')}.scale.set(${obj.scale.join(', ')});

              const mat_${obj.id.replace(/-/g, '_')} = new THREE.MeshStandardMaterial({
                color: '${obj.material.color}',
                roughness: ${obj.material.roughness},
                metalness: ${obj.material.metalness},
                emissive: '${obj.material.emissive}',
                emissiveIntensity: ${obj.material.emissiveIntensity},
                wireframe: ${obj.material.wireframe || false}
              });

              // Central Sphere (Icosahedron base)
              const centerGeo = new THREE.IcosahedronGeometry(0.5, 0);
              const centerMesh = new THREE.Mesh(centerGeo, mat_${obj.id.replace(/-/g, '_')});
              centerMesh.castShadow = true; centerMesh.receiveShadow = true;
              group_${obj.id.replace(/-/g, '_')}.add(centerMesh);

              // Surrounding Spheres
              for(let i=0; i<12; i++) {
                  const angle = (i / 12) * Math.PI * 2;
                  const radius = 1;
                  const phi = Math.acos( -1 + ( 2 * i ) / 12 );
                  const theta = Math.sqrt( 12 * Math.PI ) * phi;
                  const x = radius * Math.cos( theta ) * Math.sin( phi );
                  const y = radius * Math.sin( theta ) * Math.sin( phi );
                  const z = radius * Math.cos( phi );
                  
                  const sphereGeo = new THREE.SphereGeometry(0.2, 16, 16);
                  const sphere = new THREE.Mesh(sphereGeo, mat_${obj.id.replace(/-/g, '_')});
                  sphere.position.set(x, y, z);
                  sphere.castShadow = true; sphere.receiveShadow = true;
                  group_${obj.id.replace(/-/g, '_')}.add(sphere);
              }

              // Outer Wireframe
              const outerGeo = new THREE.IcosahedronGeometry(1, 0);
              const wireMat = mat_${obj.id.replace(/-/g, '_')}.clone();
              wireMat.wireframe = true;
              const outerMesh = new THREE.Mesh(outerGeo, wireMat);
              outerMesh.castShadow = true; outerMesh.receiveShadow = true;
              group_${obj.id.replace(/-/g, '_')}.add(outerMesh);

              group_${obj.id.replace(/-/g, '_')}.userData = { 
                id: '${obj.id}',
                physics: ${JSON.stringify(phys)},
                strength: ${strength},
                animation: ${JSON.stringify(animations)},
                speed: ${obj.animationSpeed || 1},
                basePos: [${obj.position.join(', ')}],
                baseRot: [${obj.rotation.join(', ')}],
                baseScale: [${obj.scale.join(', ')}],
                pState: { deformation: 0, lastInteractionTime: 0 }
              };
              scene.add(group_${obj.id.replace(/-/g, '_')});
              physicsObjects.push(group_${obj.id.replace(/-/g, '_')});
              `
          };
      }

      if (obj.type === 'text') {
          // Extract real URL from proxy if needed
          let fontUrl = obj.font || 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/helvetiker_regular.typeface.json';
          if (fontUrl.includes('/api/proxy-font')) {
              try {
                  const match = fontUrl.match(/url=([^&]+)/);
                  if (match && match[1]) {
                      fontUrl = decodeURIComponent(match[1]);
                  }
              } catch (e) { console.error("Error parsing font url", e); }
          }

          return {
              id: obj.id.replace(/-/g, '_'),
              init: `
              // Text Object (${obj.id})
              const loader_${obj.id.replace(/-/g, '_')} = new THREE.FontLoader();
              
              const initText_${obj.id.replace(/-/g, '_')} = (font) => {
                  const geometry = new THREE.TextGeometry('${obj.isSmallCaps ? (obj.text || 'TEXT').toUpperCase() : (obj.text || 'TEXT')}', {
                      font: font,
                      size: 0.5,
                      height: 0.2,
                      curveSegments: 12,
                      bevelEnabled: true,
                      bevelThickness: 0.02,
                      bevelSize: 0.02,
                      bevelOffset: 0,
                      bevelSegments: 5
                  });
                  geometry.center(); 
                  
                  const material = new THREE.MeshStandardMaterial({
                    color: '${obj.material.color}',
                    roughness: ${obj.material.roughness},
                    metalness: ${obj.material.metalness},
                    emissive: '${obj.material.emissive}',
                    emissiveIntensity: ${obj.material.emissiveIntensity},
                    wireframe: ${obj.material.wireframe || false}
                  });

                  const mesh = new THREE.Mesh(geometry, material);
                  mesh.position.set(${obj.position.join(', ')});
                  mesh.rotation.set(${obj.rotation.join(', ')});
                  mesh.scale.set(${obj.scale.join(', ')});
                  
                  mesh.userData = { 
                    id: '${obj.id}',
                    physics: ${JSON.stringify(phys)},
                    strength: ${strength},
                    animation: ${JSON.stringify(animations)},
                    speed: ${obj.animationSpeed || 1},
                    basePos: [${obj.position.join(', ')}],
                    baseRot: [${obj.rotation.join(', ')}],
                    baseScale: [${obj.scale.join(', ')}],
                    pState: { deformation: 0, lastInteractionTime: 0 }
                  };
                  
                  scene.add(mesh);
                  physicsObjects.push(mesh);
              };

              if (embeddedFonts['${obj.font || ''}']) {
                  initText_${obj.id.replace(/-/g, '_')}(loader_${obj.id.replace(/-/g, '_')}.parse(embeddedFonts['${obj.font || ''}']));
              } else {
                  // Defensive cascade: try the requested URL, fall back via
                  // same-origin /api/proxy-font (bypasses CORS), then to the
                  // built-in Helvetiker. Logs each failure so debugging is
                  // possible. Aesthetic preserved — only failure modes change.
                  const FALLBACK_HELVETIKER = 'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/fonts/helvetiker_regular.typeface.json';
                  const tryFontUrl_${obj.id.replace(/-/g, '_')} = (url, onSuccess, attemptsRemaining) => {
                      loader_${obj.id.replace(/-/g, '_')}.load(
                          url,
                          onSuccess,
                          undefined,
                          (err) => {
                              console.warn('[Matilda] font load failed', url, err && err.message ? err.message : err);
                              if (attemptsRemaining <= 0) return;
                              // Step 1 → proxy through same-origin /api/proxy-font
                              if (attemptsRemaining === 2 && url.indexOf('/api/proxy-font') === -1) {
                                  const proxied = '/api/proxy-font?url=' + encodeURIComponent(url);
                                  tryFontUrl_${obj.id.replace(/-/g, '_')}(proxied, onSuccess, 1);
                                  return;
                              }
                              // Step 2 → Helvetiker fallback so text always renders
                              if (attemptsRemaining >= 1) {
                                  console.warn('[Matilda] falling back to Helvetiker for text');
                                  tryFontUrl_${obj.id.replace(/-/g, '_')}(FALLBACK_HELVETIKER, onSuccess, 0);
                              }
                          }
                      );
                  };
                  tryFontUrl_${obj.id.replace(/-/g, '_')}('${fontUrl}', initText_${obj.id.replace(/-/g, '_')}, 2);
              }
              `
          };
      }

      return {
        id: obj.id.replace(/-/g, '_'),
        init: `
  // Object: ${obj.type} (${obj.id})
  const geometry_${obj.id.replace(/-/g, '_')} = ${geometryCode};
  const material_${obj.id.replace(/-/g, '_')} = new THREE.MeshStandardMaterial({
    color: '${obj.material.color}',
    roughness: ${obj.material.roughness},
    metalness: ${obj.material.metalness},
    emissive: '${obj.material.emissive}',
    emissiveIntensity: ${obj.material.emissiveIntensity},
    wireframe: ${obj.material.wireframe || false}
  });
  ${obj.material.texture ? `
  new THREE.TextureLoader().load('${obj.material.texture}', (tex) => {
    material_${obj.id.replace(/-/g, '_')}.map = tex;
    material_${obj.id.replace(/-/g, '_')}.needsUpdate = true;
  });` : ''}
  const mesh_${obj.id.replace(/-/g, '_')} = new THREE.Mesh(geometry_${obj.id.replace(/-/g, '_')}, material_${obj.id.replace(/-/g, '_')});
  mesh_${obj.id.replace(/-/g, '_')}.position.set(${obj.position.join(', ')});
  mesh_${obj.id.replace(/-/g, '_')}.rotation.set(${obj.rotation.join(', ')});
  mesh_${obj.id.replace(/-/g, '_')}.userData = { 
    id: '${obj.id}',
    physics: ${JSON.stringify(phys)},
    strength: ${strength},
    animation: ${JSON.stringify(animations)},
    speed: ${obj.animationSpeed || 1},
    basePos: [${obj.position.join(', ')}],
    baseRot: [${obj.rotation.join(', ')}],
    baseScale: [${obj.scale.join(', ')}],
    pState: { deformation: 0, lastInteractionTime: 0 }
  };
  scene.add(mesh_${obj.id.replace(/-/g, '_')});
  physicsObjects.push(mesh_${obj.id.replace(/-/g, '_')});`,
      };
    });

    const vantaType = background.startsWith('vanta-') ? background.replace('vanta-', '').toUpperCase() : null;
    const vantaScript = vantaType ? (vantaType === 'CLOUDS' ? 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.clouds.min.js' : 
                                     vantaType === 'CELLS' ? 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.cells.min.js' :
                                     vantaType === 'NET' ? 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.net.min.js' :
                                     vantaType === 'RINGS' ? 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.rings.min.js' :
                                     vantaType === 'HALO' ? 'https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.halo.min.js' : '') : '';


    const lightingCode = (() => {
      switch(lighting) {
        case 'neon': return `
  const ambientLight = new THREE.AmbientLight(0x220033, 0.2);
  scene.add(ambientLight);
  const p1 = new THREE.PointLight(0xff00ff, 5, 50); p1.position.set(10, 10, 10); scene.add(p1);
  const p2 = new THREE.PointLight(0x00ffff, 5, 50); p2.position.set(-10, -10, -10); scene.add(p2);
  const p3 = new THREE.PointLight(0xffff00, 3, 20); p3.position.set(0, 5, 0); scene.add(p3);`;
        case 'alien': return `
  const ambientLight = new THREE.AmbientLight(0x001100, 0.1);
  scene.add(ambientLight);
  const spot = new THREE.SpotLight(0x00ff00, 8, 30, 0.6, 0.5); spot.position.set(0, 20, 0); scene.add(spot);
  const p1 = new THREE.PointLight(0x800080, 5, 30); p1.position.set(10, 5, 10); scene.add(p1);
  scene.fog = new THREE.Fog('#001100', 5, 30);`;
        case 'sunlight': return `
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambientLight);
  const dir = new THREE.DirectionalLight(0xffffff, 1.5); dir.position.set(10, 10, 5); scene.add(dir);`;
        case 'studio':
        default: return `
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambientLight);
  const spot = new THREE.SpotLight(0xffffff, 1, 50, 0.15, 1); spot.position.set(10, 10, 10); scene.add(spot);
  const p1 = new THREE.PointLight(0xffffff, 0.5); p1.position.set(-10, -10, -10); scene.add(p1);`;
      }
    })();

    const camPos = cameraRef.current ? [cameraRef.current.position.x, cameraRef.current.position.y, cameraRef.current.position.z] : [0, 0, 5];
    const camRot = cameraRef.current ? [cameraRef.current.rotation.x, cameraRef.current.rotation.y, cameraRef.current.rotation.z] : [0, 0, 0];
    const camTarget = orbitControlsRef.current ? [orbitControlsRef.current.target.x, orbitControlsRef.current.target.y, orbitControlsRef.current.target.z] : [0, 0, 0];

    const jsCode = `
/**
 * Paradox 3D Generated Scene
 */
const THREE = window.THREE;
const scene = new THREE.Scene();
const physicsObjects = [];

// Embedded Fonts
const embeddedFonts = ${fontsJson};

${!vantaType ? `scene.background = new THREE.Color('${background === 'none' ? '#000000' : (background === 'green' ? CHROMA_KEY_GREEN : (background === 'stars' ? backgroundDarkColor : '#111111'))}');` : ''}

const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
camera.position.set(${camPos.join(', ')});
camera.rotation.set(${camRot.join(', ')});

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(window.devicePixelRatio);
document.body.appendChild(renderer.domElement);

const controls = new THREE.OrbitControls(camera, renderer.domElement);
controls.target.set(${camTarget.join(', ')});
controls.update();

// Lighting
${lightingCode}

// Objects
${objectsCode.map(o => o.init).join('\n')}

// Interaction
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

window.addEventListener('mousedown', (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(physicsObjects);
  if (intersects.length > 0) {
    const obj = intersects[0].object;
    if (obj.userData && obj.userData.onClick) {
        obj.userData.onClick();
    }
    if (obj.userData && obj.userData.pState) {
        obj.userData.pState.deformation = 1.0;
        obj.userData.pState.lastInteractionTime = performance.now() / 1000;
    }
  }
});

window.addEventListener('mousemove', (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(physicsObjects);
  if (intersects.length > 0) {
      const obj = intersects[0].object;
      if (obj.userData && obj.userData.onHover) {
          obj.userData.onHover();
      }
  }
});

// Vanta Background
${vantaType ? `
const vantaEffect = VANTA.${vantaType}({
  el: "body",
  mouseControls: true,
  touchControls: true,
  gyroControls: false,
  minHeight: 200.00,
  minWidth: 200.00,
  scale: 1.00,
  scaleMobile: 1.00,
  speed: ${backgroundSpeed},
  color: ${parseInt((backgroundLightColor || '#ffffff').replace('#', ''), 16)},
  backgroundColor: ${parseInt((backgroundDarkColor || '#000000').replace('#', ''), 16)},
  ${vantaType === 'CLOUDS' ? `
  skyColor: ${parseInt((vantaConfig.skyColor || '#68b8d7').replace('#', ''), 16)},
  cloudColor: ${parseInt((vantaConfig.cloudColor || '#adc1de').replace('#', ''), 16)},
  cloudShadowColor: ${parseInt((vantaConfig.cloudShadowColor || '#183550').replace('#', ''), 16)},
  sunColor: ${parseInt((vantaConfig.sunColor || '#ff9919').replace('#', ''), 16)},
  sunGlareColor: ${parseInt((vantaConfig.sunGlareColor || '#ff6633').replace('#', ''), 16)},
  sunlightColor: ${parseInt((vantaConfig.sunlightColor || '#ff9919').replace('#', ''), 16)},` : ''}
  ${vantaType === 'NET' ? `
  points: ${vantaConfig.points || 20},
  maxDistance: ${vantaConfig.maxDistance || 29},
  showDots: ${vantaConfig.showDots || false},` : ''}
});` : ''}

// Stars Background
${background === 'stars' ? `
const starCount = 3000;
const starGeometry = new THREE.BufferGeometry();
const starPositions = new Float32Array(starCount * 3);
const starSizes = new Float32Array(starCount);
const starColors = new Float32Array(starCount * 3);
const baseColor = new THREE.Color('${backgroundLightColor}');

for (let i = 0; i < starCount; i++) {
  starPositions[i * 3] = (Math.random() - 0.5) * 200;
  starPositions[i * 3 + 1] = (Math.random() - 0.5) * 200;
  starPositions[i * 3 + 2] = (Math.random() - 0.5) * 200;
  starSizes[i] = Math.random() < 0.8 ? Math.random() * 0.5 + 0.1 : Math.random() * 1.5 + 0.5;
  const c = baseColor.clone().offsetHSL(0, 0, (Math.random() - 0.5) * 0.2);
  starColors[i * 3] = c.r;
  starColors[i * 3 + 1] = c.g;
  starColors[i * 3 + 2] = c.b;
}

starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
starGeometry.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
starGeometry.setAttribute('size', new THREE.BufferAttribute(starSizes, 1));

const starCanvas = document.createElement('canvas');
starCanvas.width = 32; starCanvas.height = 32;
const starCtx = starCanvas.getContext('2d');
starCtx.fillStyle = '#ffffff';
starCtx.beginPath();
const cx = 16, cy = 16, spikes = 4, outer = 15, inner = 4;
for (let i = 0; i < spikes * 2; i++) {
  const r = i % 2 === 0 ? outer : inner;
  const a = (i / (spikes * 2)) * Math.PI * 2;
  starCtx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
}
starCtx.closePath(); starCtx.fill();
const starTexture = new THREE.CanvasTexture(starCanvas);

const starMaterial = new THREE.PointsMaterial({ 
  map: starTexture, 
  size: 1, 
  vertexColors: true, 
  transparent: true, 
  opacity: 0.9, 
  blending: THREE.AdditiveBlending,
  depthWrite: false
});
const stars = new THREE.Points(starGeometry, starMaterial);
scene.add(stars);
` : ''}

// Water Background
${background === 'water' ? `
const waterGeometry = new THREE.PlaneGeometry(10000, 10000);
const water = new THREE.Water(waterGeometry, {
  textureWidth: 512,
  textureHeight: 512,
  waterNormals: new THREE.TextureLoader().load('https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/waternormals.jpg', (texture) => {
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  }),
  sunDirection: new THREE.Vector3(),
  sunColor: new THREE.Color('${backgroundLightColor}').getHex(),
  waterColor: new THREE.Color('${backgroundDarkColor}').getHex(),
  distortionScale: 3.7,
  fog: scene.fog !== undefined
});
water.rotation.x = -Math.PI / 2;
water.position.y = -2;
scene.add(water);

const sky = new THREE.Sky();
sky.scale.setScalar(10000);
scene.add(sky);
const sun = new THREE.Vector3();
sun.setFromSphericalCoords(1, THREE.MathUtils.degToRad(88), THREE.MathUtils.degToRad(180));
sky.material.uniforms['sunPosition'].value.copy(sun);
` : ''}

function animate() {
  requestAnimationFrame(animate);
  const time = performance.now() / 1000;
  
  physicsObjects.forEach(mesh => {
    const data = mesh.userData;
    const pState = data.pState;
    const speed = data.speed;
    const phys = data.physics;
    const anims = data.animation;
    const strength = data.strength;
    
    // Reset transforms to base
    mesh.position.set(...data.basePos);
    mesh.rotation.set(...data.baseRot);
    mesh.scale.set(...data.baseScale);
    
    // Animations
    if (anims.includes('spin')) mesh.rotation.y += time * speed;
    if (anims.includes('bounce')) mesh.position.y += Math.abs(Math.sin(time * 2 * speed)) * 1;
    if (anims.includes('pulse')) {
      const f = 1 + Math.sin(time * 3 * speed) * 0.2;
      mesh.scale.multiplyScalar(f);
    }
    if (anims.includes('float')) mesh.position.y += Math.sin(time * speed) * 0.3;
    if (anims.includes('orbit')) {
      mesh.position.x += Math.sin(time * speed) * 3;
      mesh.position.z += Math.cos(time * speed) * 3;
    }
    
    // Physics
    if (phys.includes('viscous-jiggle') && pState.deformation > 0.001) {
      const t = time - pState.lastInteractionTime;
      const decay = Math.exp(-t * 3);
      const wave = Math.sin(t * 25) * 0.3 * decay * strength;
      mesh.scale.x += wave;
      mesh.scale.y -= wave;
      mesh.scale.z += wave;
      if (decay < 0.01) pState.deformation = 0;
    }
  });
  
  if ('${background}' === 'stars' && typeof stars !== 'undefined') {
    stars.rotation.y += 0.0005 * ${backgroundSpeed};
    const positions = stars.geometry.attributes.position.array;
    for (let i = 0; i < 3000; i++) {
        positions[i * 3 + 2] += ${backgroundSpeed} * 0.2;
        if (positions[i * 3 + 2] > 100) positions[i * 3 + 2] = -100;
    }
    stars.geometry.attributes.position.needsUpdate = true;
  }
  
  if ('${background}' === 'water' && typeof water !== 'undefined') {
    water.material.uniforms['time'].value += (1.0 / 60.0) * ${backgroundSpeed};
  }
  
  renderer.render(scene, camera);
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

animate();
    `;

    const cssCode = `
body { margin: 0; overflow: hidden; background: #000; font-family: sans-serif; width: 100vw; height: 100vh; }
canvas { display: block; position: relative; z-index: 1; }
    `;

    const htmlCode = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Paradox 3D Scene</title>
    <link rel="stylesheet" href="style.css">
    <script src="https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/controls/OrbitControls.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/loaders/FontLoader.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/geometries/TextGeometry.js"></script>
    ${background === 'water' ? `<script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/objects/Water.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/objects/Sky.js"></script>` : ''}
    ${vantaScript ? `<script src="${vantaScript}"></script>` : ''}
</head>
<body>
    <script src="./scene.js"></script>
</body>
</html>
    `;

    return { js: jsCode.trim(), html: htmlCode.trim(), css: cssCode.trim() };
  };

  // Update code content when objects change or active file changes
  useEffect(() => {
      if (showCode) {
          const generated = generateCode();
          const content = userEdits[activeCodeFile] || generated[activeCodeFile];
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setCodeContent(content);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, showCode, activeCodeFile, userEdits]);

  const handleUpdateCode = () => {
      // Sync back to scene state
      if (activeCodeFile === 'js') {
          try {
              // 1. Sync Background
              const bgMatch = codeContent.match(/scene\.background = new THREE\.Color\(['"](.+?)['"]\)/);
              if (bgMatch && bgMatch[1]) {
                  setBackgroundDarkColor(bgMatch[1]);
              }
              
              const lightColorMatch = codeContent.match(/color:\s*0x([0-9a-fA-F]+)/) || codeContent.match(/sunColor:\s*0x([0-9a-fA-F]+)/);
              if (lightColorMatch && lightColorMatch[1]) {
                  setBackgroundLightColor('#' + lightColorMatch[1]);
              }

              const speedMatch = codeContent.match(/speed:\s*([0-9.]+)/);
              if (speedMatch && speedMatch[1]) {
                  setBackgroundSpeed(parseFloat(speedMatch[1]));
              }

              // 2. Sync Objects (Deep copy for React state updates)
              let objectsChanged = false;
              const newObjects = objects.map(obj => {
                  const id = obj.id.replace(/-/g, '_');
                  const updatedObj = { ...obj };
                  let changed = false;
                  
                  // Position
                  const posRegex = new RegExp(`mesh_${id}\\.position\\.set\\((.+?)\\)`);
                  const posMatch = codeContent.match(posRegex);
                  if (posMatch) {
                      const parts = posMatch[1].split(',').map(s => parseFloat(s.trim()));
                      if (parts.length === 3 && !parts.some(isNaN)) {
                          updatedObj.position = [parts[0], parts[1], parts[2]];
                          changed = true;
                      }
                  }

                  // Rotation
                  const rotRegex = new RegExp(`mesh_${id}\\.rotation\\.set\\((.+?)\\)`);
                  const rotMatch = codeContent.match(rotRegex);
                  if (rotMatch) {
                      const parts = rotMatch[1].split(',').map(s => parseFloat(s.trim()));
                      if (parts.length === 3 && !parts.some(isNaN)) {
                          updatedObj.rotation = [parts[0], parts[1], parts[2]];
                          changed = true;
                      }
                  }

                  // Color
                  const colorRegex = new RegExp(`material_${id} = new THREE\\.MeshStandardMaterial\\(\\{[\\s\\S]*?color: '(.+?)'`);
                  const colorMatch = codeContent.match(colorRegex);
                  if (colorMatch && colorMatch[1]) {
                      updatedObj.material = { ...updatedObj.material, color: colorMatch[1] };
                      changed = true;
                  }

                  if (changed) objectsChanged = true;
                  return updatedObj;
              });

              if (objectsChanged) {
                  setObjects(newObjects);
                  addToHistory(newObjects);
              }
              
              // 3. Sync Camera
              const camMatch = codeContent.match(/camera\.position\.set\((.+?)\)/);
              if (camMatch && camMatch[1]) {
                  const parts = camMatch[1].split(',').map(s => parseFloat(s.trim()));
                  if (parts.length === 3 && !parts.some(isNaN)) {
                      if (cameraRef.current) {
                          cameraRef.current.position.set(parts[0], parts[1], parts[2]);
                      }
                  }
              }

              // CRITICAL: After successfully syncing JS changes to state, clear the userEdits override
              // so that subsequent canvas interactions (which update state) will regenerate the code
              // and update the editor. This restores the "live link" between canvas and code.
              setUserEdits(prev => {
                  const next = { ...prev };
                  delete next['js'];
                  return next;
              });
              
              setNotification({ message: "Code Synced & Live Linked", type: 'success' });
              setTimeout(() => setNotification(null), 3000);

          } catch (e) {
              console.warn("Failed to sync some code changes back to editor state", e);
              // If sync fails, save as manual edit so work isn't lost
              setUserEdits(prev => ({ ...prev, [activeCodeFile]: codeContent }));
              setNotification({ message: "Sync Failed: Editor Locked", type: 'error' });
              setTimeout(() => setNotification(null), 3000);
          }
      } else {
          // For CSS/HTML, we can't sync state, so we must save as override
          setUserEdits(prev => ({ ...prev, [activeCodeFile]: codeContent }));
          setNotification({ message: "Code Updated (Manual Mode)", type: 'success' });
          setTimeout(() => setNotification(null), 3000);
      }
  };

  // Silence Vite HMR WebSocket errors which are expected in this environment
  useEffect(() => {
      const handleError = (event: PromiseRejectionEvent) => {
          if (event.reason?.message?.includes('failed to connect to websocket') || 
              event.reason?.message?.includes('WebSocket closed without opened')) {
              event.preventDefault();
          }
      };
      window.addEventListener('unhandledrejection', handleError);
      return () => window.removeEventListener('unhandledrejection', handleError);
  }, []);

  const fetchFonts = async () => {
    const fontData: Record<string, unknown> = {};
    const textObjects = objects.filter(obj => obj.type === 'text' && obj.font);
    const uniqueFonts = [...new Set(textObjects.map(obj => obj.font!))];

    for (const fontUrl of uniqueFonts) {
        try {
            const response = await fetch(fontUrl);
            if (response.ok) {
                fontData[fontUrl] = await response.json();
            }
        } catch (e) {
            console.error("Failed to pre-fetch font for export:", fontUrl, e);
        }
    }
    return fontData;
  };

  const handleExportBundle = async () => {
      try {
          const fontData = await fetchFonts();
          const generated = generateCode(fontData);
          
          // Capture current state of all files
          const files = {
              js: userEdits.js || generated.js,
              html: userEdits.html || generated.html,
              css: userEdits.css || generated.css
          };

          // If code editor is active, ensure we have the most recent content for the active file
          if (showCode && codeContent) {
              files[activeCodeFile] = codeContent;
          }

          const zip = new JSZip();
          
          // Ensure index.html is never blank
          let finalHtml = (files.html || "").trim();
          if (!finalHtml || finalHtml.length < 10) { // If suspiciously short or empty
              finalHtml = generated.html || `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Paradox 3D Scene</title>
    <link rel="stylesheet" href="style.css">
    <script src="https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/controls/OrbitControls.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/loaders/FontLoader.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/three@0.134.0/examples/js/geometries/TextGeometry.js"></script>
</head>
<body>
    <script src="./scene.js"></script>
</body>
</html>`;
          }

          zip.file("scene.js", files.js || generated.js);
          zip.file("index.html", finalHtml);
          zip.file("style.css", files.css || generated.css);

          const content = await zip.generateAsync({ 
              type: "blob",
              compression: "DEFLATE",
              compressionOptions: { level: 9 }
          });
          
          saveAs(content, `paradox_bundle_${Date.now()}.zip`);
      } catch (err) {
          console.error("Bundle Export Error:", err);
          alert("Failed to generate export bundle. See console for details.");
      }
  }

  const getContainerStyle = () => {
    switch (aspectRatio) {
      case '1:1': return 'aspect-square max-h-full -translate-y-[35px] md:translate-y-0';
      case '9:16': return 'aspect-[9/16] h-full -translate-y-[25px] md:translate-y-0';
      case '16:9': return 'aspect-video w-full -translate-y-[35px] md:translate-y-0';
      default: return 'h-full w-full';
    }
  };

  const handleBackgroundChange = (val: string) => {
      if (val === 'imagine') {
          setShowImagineModal(true);
          return;
      }
      if (val === 'green') {
          setBackgroundLightColor(CHROMA_KEY_GREEN);
      }
      if (val === 'vanta-clouds') {
          // Set default speed to 0.5 (mapped to slider)
          setBackgroundSpeed(0.5);
      }
      setBackground(val as BackgroundType);
      // Reset defaults if needed, or keep previous colors
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          // Upload to GCP Cloud Storage
          try {
              const { default: GCPService } = await import('../../services/gcp');
              
              // Ensure user is authenticated
              if (!GCPService.isAuthenticated()) {
                  await GCPService.initializeAuth();
              }

              const isObj = file.name.toLowerCase().endsWith('.obj');
              const modelType = isObj ? 'obj' : 'gltf';
              
              // Upload to GCP
              const response = await GCPService.uploadFile(file, 'model');
              const url = response.signedUrl; // Use signed URL that's valid for 7 days

              // If we are currently on an upload type, replace it, otherwise add new
              if (selectedObject.type === 'upload') {
                  updateSelectedObject({ modelUrl: url, modelType });
              } else {
                  addObject('upload', undefined, url, modelType);
              }
          } catch (error) {
              console.error('File upload failed:', error);
              // Fallback to blob URL if GCP fails
              const url = URL.createObjectURL(file);
              const isObj = file.name.toLowerCase().endsWith('.obj');
              const modelType = isObj ? 'obj' : 'gltf';
              
              if (selectedObject.type === 'upload') {
                  updateSelectedObject({ modelUrl: url, modelType });
              } else {
                  addObject('upload', undefined, url, modelType);
              }
          }
      }
  };

  const finalVantaConfig = useMemo(() => ({
      ...vantaConfig, 
      speed: backgroundSpeed,
      lightColor: backgroundLightColor,
      backgroundColor: backgroundDarkColor
  }), [vantaConfig, backgroundSpeed, backgroundLightColor, backgroundDarkColor]);

  if (fitContainer) {
    return (
      <div 
        ref={containerRef} 
        className={cn("relative overflow-hidden w-full h-full bg-[#0a0a0a] font-small-caps", getContainerStyle())}
      >
        <VantaBackground type={background} config={finalVantaConfig} />
        <Canvas 
            className="hue-exclude"
            shadows 
            camera={{ position: [0, 0, 5], fov: 50 }} 
            gl={{ preserveDrawingBuffer: true, alpha: true }}
        >
            <CameraCapture cameraRef={cameraRef} />
            <LightingController type={lighting} />
            <BackgroundController type={background} speed={backgroundSpeed} lightColor={backgroundLightColor} darkColor={backgroundDarkColor} vantaConfig={vantaConfig} />
            
            <Suspense fallback={null}>
            <group ref={sceneRef}>
                {objects.map(obj => (
                    <AnimatedMesh 
                        key={obj.id} 
                        obj={obj} 
                        isSelected={false}
                        objects={objects}
                        isAnnotatorActive={false}
                        onHover={() => {}}
                        onAnnotateSelect={() => {}}
                        onSelect={() => {}}
                        onUpdate={() => {}}
                        showTransformControls={false}
                        transformMode={transformMode}
                    />
                ))}
            </group>
            {lighting === 'studio' && (
                <EnvironmentErrorBoundary>
                    <Environment preset="city" />
                </EnvironmentErrorBoundary>
            )}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
                <planeGeometry args={[100, 100]} />
                <shadowMaterial transparent opacity={0.4} />
            </mesh>
            </Suspense>
            
            <OrbitControls 
                ref={orbitControlsRef}
                makeDefault 
                enabled={true} 
            />
        </Canvas>
      </div>
    );
  }

  return (
    <div id="creator-3d-viewport" data-theme="dark" className="relative flex h-full w-full items-center justify-center bg-neutral-900 font-small-caps overflow-hidden">
      {/* Mobile Menu Backdrop */}
      {showMobileMenu && (
          <div 
            className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm md:hidden animate-in fade-in duration-300" 
            onClick={() => setShowMobileMenu(false)}
          />
      )}

      {/* Top Left Controls */}
      <div className={cn(
          "absolute top-2 sm:top-4 left-2 sm:left-4 z-[80] flex items-center gap-2 transition-all duration-300",
          !isLeftPanelMinimized && "md:translate-x-72 md:ml-4"
      )}>
          {/* Mobile Menu Toggle */}
          <div className="md:hidden">
              <Button size="icon" variant="secondary" className="h-8 w-8 sm:h-10 sm:w-10" onClick={(e) => { e.stopPropagation(); setShowMobileMenu(!showMobileMenu); }}>
                  {showMobileMenu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
          </div>

          {/* Desktop Left Panel Toggle (Restore) */}
          {isLeftPanelMinimized && (
            <div className="hidden md:block">
                <Button size="icon" variant="secondary" className="h-8 w-8 sm:h-10 sm:w-10" onClick={() => setIsLeftPanelMinimized(false)}>
                    <LayersIcon className="h-4 w-4" />
                </Button>
            </div>
          )}
      </div>

      {/* Left Toolbar */}
      <div className={cn(
          "absolute top-4 left-4 z-[70] flex flex-col gap-4 w-72 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 pb-4 scrollbar-hide transition-all duration-300",
          showMobileMenu 
            ? "translate-x-0 mt-10 p-4 rounded-lg w-[calc(100vw-2rem-4px)] max-h-[calc(100vh-13.5rem-4px)]" 
            : cn("-translate-x-[120%]", isLeftPanelMinimized ? "md:-translate-x-[120%]" : "md:translate-x-0")
      )}>
        {/* Desktop Minimize Button */}
        <div className="hidden md:flex justify-end mb-2">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setIsLeftPanelMinimized(true)}>
                <ChevronDown className="h-4 w-4 rotate-90" />
            </Button>
        </div>

        {/* Layering Window */}
        <CollapsiblePanel title="Layers" icon={LayersIcon} defaultOpen={true}>
          <div className="flex items-center justify-between text-xs font-semibold uppercase text-muted-foreground mb-2">
             <span>Objects</span>
             <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => addObject('cube')} title="Add Layer"><Plus className="w-3 h-3" /></Button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {objects.map((obj, i) => (
                <div 
                    key={obj.id} 
                    className={cn(
                        "flex items-center justify-between p-2 rounded text-xs cursor-pointer transition-colors",
                        selectedId === obj.id ? "bg-white/20 text-white" : "hover:bg-white/5"
                    )}
                    onClick={() => setSelectedId(obj.id)}
                >
                    <span>{obj.type.charAt(0).toUpperCase() + obj.type.slice(1)} {i + 1}</span>
                    <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-5 w-5 text-muted-foreground hover:text-destructive" 
                        onClick={(e) => { e.stopPropagation(); removeObject(obj.id); }}
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                </div>
            ))}
          </div>
                    {/* Background & Lighting Selection */}
          <div className="pt-2 border-t border-white/10 space-y-2">
             <div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
                <ImageIcon className="w-3 h-3" /> Environment
             </div>
             <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <label className="text-[10px] text-muted-foreground">Background</label>
                    <select 
                        className="w-full h-7 rounded border border-white/10 bg-neutral-800 px-1 text-[10px] capitalize text-white"
                        value={background}
                        onChange={(e) => handleBackgroundChange(e.target.value)}
                    >
                        {BACKGROUND_OPTIONS.map(bg => (
                            <option key={bg} value={bg}>{bg.replace('vanta-', '').replace(/-/g, ' ')}</option>
                        ))}
                        <option value="imagine">✨ Imagine...</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] text-muted-foreground">Lighting</label>
                    <select 
                        className="w-full h-7 rounded border border-white/10 bg-neutral-800 px-1 text-[10px] capitalize text-white"
                        value={lighting}
                        onChange={(e) => setLighting(e.target.value as LightingType)}
                    >
                        {LIGHTING_OPTIONS.map(l => (
                            <option key={l} value={l}>{l}</option>
                        ))}
                    </select>
                </div>
             </div>
             {/* Background Speed Slider */}
             <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>Speed</span>
                    <span>{backgroundSpeed.toFixed(1)}x</span>
                </div>
                <input 
                    type="range" min="0" max="5" step="0.1" 
                    value={backgroundSpeed}
                    onChange={(e) => setBackgroundSpeed(parseFloat(e.target.value))}
                    disabled={['none', 'green'].includes(background)}
                    className={cn(
                        "w-full h-1.5 rounded-lg appearance-none cursor-pointer",
                        ['none', 'green'].includes(background) ? "bg-neutral-700" : "bg-white/50"
                    )}
                />
             </div>

             {(!background.startsWith('vanta-') || ['vanta-net', 'vanta-rings', 'vanta-halo', 'vanta-cells'].includes(background)) && (
                 <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
                     <div className="space-y-1">
                        <label className="text-[10px] text-muted-foreground">{background === 'green' ? 'Chroma Key' : 'Light'}</label>
                        <input 
                            type="color" 
                            value={backgroundLightColor}
                            onChange={(e) => setBackgroundLightColor(e.target.value)}
                            className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                        />
                     </div>
                     {background !== 'green' && (
                         <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground">Dark</label>
                            <input 
                                type="color" 
                                value={backgroundDarkColor}
                                onChange={(e) => setBackgroundDarkColor(e.target.value)}
                                className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                            />
                         </div>
                     )}
                 </div>
             )}

             {/* Vanta Specific Controls */}
             {background.startsWith('vanta-') && (
                 <div className="pt-2 border-t border-white/5 space-y-2">
                     {background === 'vanta-clouds' && (
                         <div className="grid grid-cols-2 gap-2">
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Sky</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.skyColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, skyColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Cloud</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.cloudColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, cloudColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Cloud Shadow</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.cloudShadowColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, cloudShadowColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Sun</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.sunColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, sunColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Sun Glare</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.sunGlareColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, sunGlareColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[10px] text-muted-foreground">Sunlight</label>
                                <input 
                                    type="color" 
                                    value={vantaConfig.sunlightColor}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, sunlightColor: e.target.value }))}
                                    className="h-6 w-full rounded cursor-pointer border-0 p-0 hue-exclude"
                                />
                             </div>
                         </div>
                     )}

                     {background === 'vanta-net' && (
                         <div className="space-y-2">
                             <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                    <span>Points</span>
                                    <span>{vantaConfig.points}</span>
                                </div>
                                <input 
                                    type="range" min="5" max="20" step="1" 
                                    value={vantaConfig.points}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, points: parseFloat(e.target.value) }))}
                                    className="w-full h-1.5 bg-white/50 rounded-lg appearance-none cursor-pointer"
                                />
                             </div>
                             <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                    <span>Distance</span>
                                    <span>{vantaConfig.maxDistance}</span>
                                </div>
                                <input 
                                    type="range" min="5" max="40" step="1" 
                                    value={vantaConfig.maxDistance}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, maxDistance: parseFloat(e.target.value) }))}
                                    className="w-full h-1.5 bg-white/50 rounded-lg appearance-none cursor-pointer"
                                />
                             </div>
                             <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                    <span>Spacing</span>
                                    <span>{vantaConfig.spacing}</span>
                                </div>
                                <input 
                                    type="range" min="5" max="40" step="1" 
                                    value={vantaConfig.spacing}
                                    onChange={(e) => setVantaConfig(prev => ({ ...prev, spacing: parseFloat(e.target.value) }))}
                                    className="w-full h-1.5 bg-white/50 rounded-lg appearance-none cursor-pointer"
                                />
                             </div>
                             <div className="flex items-center gap-2 pt-1">
                                  <input 
                                      type="checkbox" 
                                      id="net-dots"
                                      checked={vantaConfig.showDots}
                                      onChange={(e) => setVantaConfig(prev => ({ ...prev, showDots: e.target.checked }))}
                                      className="h-3 w-3 rounded border-white/10 bg-neutral-800"
                                  />
                                  <label htmlFor="net-dots" className="text-[10px] text-muted-foreground cursor-pointer select-none">Show Dots</label>
                              </div>
                         </div>
                     )}

                 </div>
             )}
          </div>
        </CollapsiblePanel>

        {/* Shape Selection */}
        <CollapsiblePanel title="Shape Type" icon={BoxIcon} defaultOpen={true}>
            <div className="space-y-3">
                <div className="flex gap-2">
                    <select 
                        className="flex-1 h-9 rounded border border-white/10 bg-neutral-800 px-2 text-sm capitalize text-white"
                        value={selectedObject.type}
                        onChange={(e) => {
                            updateSelectedObject({ type: e.target.value as ShapeType });
                        }}
                    >
                        {selectedObject.type === 'upload' && (
                            <option value="upload">Uploaded Model</option>
                        )}
                        {SHAPE_OPTIONS.map(shape => (
                            <option key={shape} value={shape}>{shape.charAt(0).toUpperCase() + shape.slice(1)}</option>
                        ))}
                    </select>
                    
                    <label className="flex h-9 w-9 items-center justify-center rounded border border-white/10 bg-neutral-800 text-white cursor-pointer hover:bg-neutral-700 transition-colors shrink-0" title="Upload 3D Model">
                        <Upload className="w-4 h-4" />
                        <input 
                            type="file" 
                            accept=".glb,model/gltf-binary,.gltf,model/gltf+json,.obj" 
                            className="hidden" 
                            onChange={handleFileUpload}
                        />
                    </label>
                </div>
                {selectedObject.type === 'upload' && (
                    <p className="text-[10px] text-muted-foreground italic">
                        Currently showing an uploaded model. Use the button above to replace it.
                    </p>
                )}
            </div>
          
          {selectedObject.type === 'text' && (
              <div className="space-y-2">
                  <div className="space-y-1">
                      <label className="text-[10px] text-muted-foreground">Text Content</label>
                      <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={selectedObject.text || ''}
                            onChange={(e) => updateSelectedObject({ text: e.target.value })}
                            className="w-full h-8 rounded border border-white/10 bg-neutral-800 px-2 text-xs text-white"
                            placeholder="Type text..."
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                          <input 
                              type="checkbox" 
                              id="small-caps"
                              checked={selectedObject.isSmallCaps || false}
                              onChange={(e) => updateSelectedObject({ isSmallCaps: e.target.checked })}
                              className="h-3 w-3 rounded border-white/10 bg-neutral-800"
                          />
                          <label htmlFor="small-caps" className="text-[10px] text-muted-foreground cursor-pointer select-none">Small Caps</label>
                      </div>
                  </div>
                  <div className="space-y-1">
                      <label className="text-[10px] text-muted-foreground">Font</label>
                      <select 
                          className="w-full h-8 rounded border border-white/10 bg-neutral-800 px-2 text-xs text-white"
                          value={selectedObject.font || FONT_OPTIONS[0].value}
                          onChange={(e) => updateSelectedObject({ font: e.target.value })}
                      >
                          {FONT_OPTIONS.map(font => (
                              <option key={font.label} value={font.value}>{font.label}</option>
                          ))}
                      </select>
                  </div>
              </div>
          )}
        </CollapsiblePanel>

        {/* Physics */}
        <CollapsiblePanel title="Physics" icon={BoxIcon} defaultOpen={true}>
            <div className="space-y-2">
                <div className="space-y-1">
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Strength</span>
                        <span>{(selectedObject.physicsStrength || 1).toFixed(1)}x</span>
                    </div>
                    <input 
                        type="range" min="0.1" max="3" step="0.1" 
                        value={selectedObject.physicsStrength || 1}
                        onChange={(e) => updateSelectedObject({ physicsStrength: parseFloat(e.target.value) })}
                        className="w-full h-1.5 bg-white/50 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
                <label className="text-[10px] text-muted-foreground font-bold uppercase">Object Physics</label>
                <div className="relative">
                    <div className="max-h-[150px] overflow-y-auto rounded border border-white/10 bg-neutral-800 p-1 space-y-1">
                        {PHYSICS_OPTIONS.map(phys => (
                            <div 
                                key={phys}
                                className={cn(
                                    "flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs cursor-pointer transition-colors",
                                    (selectedObject.physics || []).includes(phys) 
                                        ? "bg-white/20 text-white border border-white/20" 
                                        : "hover:bg-white/5 text-primary/60"
                                )}
                                onClick={() => {
                                    const current = selectedObject.physics || [];
                                    const newPhysics = current.includes(phys)
                                        ? current.filter(p => p !== phys)
                                        : [...current, phys];
                                    updateSelectedObject({ physics: newPhysics });
                                }}
                            >
                                <span>{PHYSICS_LABELS[phys].label}</span>
                                <span className="text-[9px] opacity-60 bg-black/20 px-1.5 py-0.5 rounded">{PHYSICS_LABELS[phys].disclaimer}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </CollapsiblePanel>

        {/* Animation */}
        <CollapsiblePanel title="Animation" icon={Move} defaultOpen={true}>
          <div className="space-y-2">
            <label className="text-[10px] text-muted-foreground font-bold uppercase">Object Animation</label>
            <div className="relative">
                {/* Custom Multi-Select Dropdown */}
                <div className="max-h-[100px] overflow-y-auto rounded border border-white/10 bg-neutral-800 p-1 space-y-1">
                    {ANIMATION_OPTIONS.map(anim => {
                        if (anim === 'none') return null;
                        const isSelected = Array.isArray(selectedObject.animation) 
                            ? selectedObject.animation.includes(anim) 
                            : selectedObject.animation === anim;
                        
                        return (
                            <div 
                                key={anim}
                                className={cn(
                                    "flex items-center justify-between px-2 py-1.5 rounded text-xs cursor-pointer transition-colors",
                                    isSelected ? "bg-white/20 text-white" : "hover:bg-white/5 text-white"
                                )}
                                onClick={() => {
                                    let current = Array.isArray(selectedObject.animation) ? selectedObject.animation : (selectedObject.animation && selectedObject.animation !== 'none' ? [selectedObject.animation] : []);
                                    if (isSelected) {
                                        current = current.filter(a => a !== anim);
                                    } else {
                                        current = [...current, anim];
                                    }
                                    updateSelectedObject({ animation: current });
                                }}
                            >
                                <span>{ANIMATION_LABELS[anim] || anim}</span>
                                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white/50" />}
                            </div>
                        );
                    })}
                </div>
            </div>
          </div>
          <div className="space-y-1 pt-2">
             <div className="flex justify-between text-xs text-muted-foreground">
                <span>Speed</span>
                <span>{selectedObject.animationSpeed?.toFixed(1) || '1.0'}x</span>
             </div>
             <input 
                type="range" min="0.1" max="5" step="0.1" 
                value={selectedObject.animationSpeed || 1}
                onChange={(e) => updateSelectedObject({ animationSpeed: parseFloat(e.target.value) })}
                className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
             />
          </div>
        </CollapsiblePanel>

        {/* Materials & Shaders */}
        <CollapsiblePanel title="Material & Shaders" icon={Palette} defaultOpen={true}>
          <div className="relative mb-2">
            <div className="max-h-[100px] overflow-y-auto rounded border border-white/10 bg-neutral-800 p-1 space-y-1">
                {['standard', 'distort', 'wobble', 'edges', 'wireframed'].map(m => {
                    const isSelected = selectedObject.material.type.includes(m as MaterialType);
                    return (
                        <div 
                            key={m}
                            className={cn(
                                "flex items-center justify-between px-2 py-1.5 rounded text-xs cursor-pointer transition-colors",
                                isSelected ? "bg-white/20 text-white" : "hover:bg-white/5 text-white"
                            )}
                            onClick={() => {
                                let current = [...selectedObject.material.type];
                                if (isSelected) {
                                    current = current.filter(t => t !== m);
                                    if (current.length === 0) current = ['standard'];
                                } else {
                                    current.push(m as MaterialType);
                                }
                                updateSelectedObject({ type: current });
                            }}
                        >
                            <span>{m.charAt(0).toUpperCase() + m.slice(1)}</span>
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white/50" />}
                        </div>
                    );
                })}
            </div>
          </div>

          {/* Color */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Base Color</label>
            <div className="flex gap-2">
                <input 
                    type="color" 
                    value={selectedObject.material.color} 
                    onChange={(e) => updateSelectedObject({ color: e.target.value })} 
                    className="h-8 w-12 rounded cursor-pointer border-0 p-0 hue-exclude" 
                />
                <input 
                    type="text" 
                    value={selectedObject.material.color} 
                    onChange={(e) => updateSelectedObject({ color: e.target.value })} 
                    className="flex-1 h-8 rounded border border-white/10 bg-neutral-800 px-2 text-xs text-white "
                />
            </div>
          </div>

          {/* Sliders */}
          <div className="space-y-3">
             <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Roughness</span>
                    <span>{selectedObject.material.roughness.toFixed(2)}</span>
                </div>
                <input 
                    type="range" min="0" max="1" step="0.01" 
                    value={selectedObject.material.roughness}
                    onChange={(e) => updateSelectedObject({ roughness: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
             </div>
             <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Metalness</span>
                    <span>{selectedObject.material.metalness.toFixed(2)}</span>
                </div>
                <input 
                    type="range" min="0" max="1" step="0.01" 
                    value={selectedObject.material.metalness}
                    onChange={(e) => updateSelectedObject({ metalness: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
             </div>
             <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Emissive</span>
                    <span>{selectedObject.material.emissiveIntensity.toFixed(2)}</span>
                </div>
                <input 
                    type="range" min="0" max="5" step="0.1" 
                    value={selectedObject.material.emissiveIntensity}
                    onChange={(e) => updateSelectedObject({ emissiveIntensity: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
             </div>
             <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Ambient Occlusion</span>
                    <span>{selectedObject.material.ambientOcclusion?.toFixed(2) || '1.00'}</span>
                </div>
                <input 
                    type="range" min="0" max="2" step="0.1" 
                    value={selectedObject.material.ambientOcclusion || 1}
                    onChange={(e) => updateSelectedObject({ ambientOcclusion: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
             </div>
             <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Scale</span>
                    <span>{selectedObject.scale[0].toFixed(1)}x</span>
                </div>
                <input 
                    type="range" min="0.1" max="5" step="0.1" 
                    value={selectedObject.scale[0]}
                    onChange={(e) => {
                        const s = parseFloat(e.target.value);
                        updateSelectedObject({ scale: [s, s, s] });
                    }}
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
             </div>
          </div>
        </CollapsiblePanel>

        {/* Textures */}
        <CollapsiblePanel title="Texture" icon={ImageIcon} defaultOpen={true}>
          <div className="">
            <TextureSelector 
                value={selectedObject.material.texture || ''}
                onChange={(val) => updateSelectedObject({ texture: val })}
            />
          </div>
        </CollapsiblePanel>

        {/* Posing / Rigging Panel - REMOVED */}
      </div>

      {/* Save Modal */}
      <Modal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        title="Save Scene"
        footer={null} // Custom footer in body
      >
          <div className="space-y-4">
              <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Scene Name</label>
                  <Input 
                      value={saveName} 
                      onChange={(e) => setSaveName(e.target.value)} 
                      placeholder="Enter scene name..."
                      className="bg-neutral-800 border-white/10"
                  />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                  <Button 
                      variant="secondary" 
                      className="h-20 flex flex-col gap-2 hover:bg-white/10 border border-white/5"
                      onClick={() => confirmSave('cache')}
                  >
                      <HardDrive className="w-6 h-6 text-blue-400" />
                      <div className="text-center">
                          <div className="text-xs font-bold">Save to Cache</div>
                          <div className="text-[10px] text-muted-foreground">Local Browser Storage</div>
                      </div>
                  </Button>
                  <Button 
                      variant="secondary" 
                      className="h-20 flex flex-col gap-2 hover:bg-white/10 border border-white/5"
                      onClick={() => confirmSave('cloud')}
                  >
                      <Cloud className="w-6 h-6 text-white" />
                      <div className="text-center">
                          <div className="text-xs font-bold">Save to Cloud</div>
                          <div className="text-[10px] text-muted-foreground">Sync across devices</div>
                      </div>
                  </Button>
              </div>
          </div>
      </Modal>

      {/* Top Right Toolbar */}
      <div className="absolute top-12 sm:top-14 right-2 sm:right-4 z-50 flex flex-col gap-2 items-end">
        <HeaderControls 
            aspectRatio={aspectRatio} 
            setAspectRatio={(r) => setAspectRatio(r as AspectRatio)} 
            onLoadAsset={(asset) => {
                // Load logic for 3D scenes if applicable
                // For now we just log, or implement full scene loading if structure matches
                console.log("Loaded asset in Paradox 3D:", asset);
            }}
            onSave={handleSaveScene}
        />
        <div className="flex gap-2 transition-all duration-300">
            {/* Minimize Toggle (Mobile Only) */}
            <div className="md:hidden">
                <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setIsToolsMinimized(!isToolsMinimized)}>
                    {isToolsMinimized ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
            </div>

            <div className={cn("flex gap-2 items-center transition-all duration-300 ease-in-out", isToolsMinimized ? "max-w-0 opacity-0 overflow-hidden md:max-w-none md:opacity-100 md:overflow-visible" : "max-w-[100vw] opacity-100")}>
                {/* Scrollable Tools Part */}
                <div className="flex gap-2 overflow-x-auto max-w-[calc(100vw-200px)] md:max-w-none pb-2 md:pb-0 no-scrollbar items-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7 md:h-9 md:w-9 shrink-0" onClick={handleNewScene} title="New Scene"><Plus className="h-3 w-3 md:h-4 md:w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 md:h-9 md:w-9 shrink-0" onClick={handleUndo} disabled={undoStack.length === 0} title="Undo"><Undo className="h-3 w-3 md:h-4 md:w-4" /></Button>
                    
                    {/* Transform Controls */}
                    <div className="flex gap-1 glass-panel rounded-lg p-1 shrink-0">
                        <Button 
                            size="icon" 
                            className="h-7 w-7 md:h-9 md:w-9"
                            variant={showAxis && transformMode === 'translate' ? "default" : "ghost"} 
                            onClick={() => { 
                                if (showAxis && transformMode === 'translate') {
                                    setShowAxis(false);
                                } else {
                                    setShowAxis(true); 
                                    setTransformMode('translate'); 
                                }
                            }} 
                            title="Move"
                        >
                            <Move className="h-3 w-3 md:h-4 md:w-4" />
                        </Button>
                        <Button 
                            size="icon" 
                            className="h-7 w-7 md:h-9 md:w-9"
                            variant={showAxis && transformMode === 'rotate' ? "default" : "ghost"} 
                            onClick={() => { 
                                if (showAxis && transformMode === 'rotate') {
                                    setShowAxis(false);
                                } else {
                                    setShowAxis(true); 
                                    setTransformMode('rotate'); 
                                }
                            }} 
                            title="Rotate"
                        >
                            <Rotate3d className="h-3 w-3 md:h-4 md:w-4" />
                        </Button>
                        <Button 
                            size="icon" 
                            className="h-7 w-7 md:h-9 md:w-9"
                            variant={showAxis && transformMode === 'scale' ? "default" : "ghost"} 
                            onClick={() => { 
                                if (showAxis && transformMode === 'scale') {
                                    setShowAxis(false);
                                } else {
                                    setShowAxis(true); 
                                    setTransformMode('scale'); 
                                }
                            }} 
                            title="Scale"
                        >
                            <Scaling className="h-3 w-3 md:h-4 md:w-4" />
                        </Button>
                    </div>

                    <Button 
                        size="icon" 
                        className="h-7 w-7 md:h-9 md:w-9 shrink-0"
                        variant={isDrawing ? "default" : "secondary"} 
                        onClick={() => setIsDrawing(!isDrawing)} 
                        title={isDrawing ? "Stop Drawing" : "Draw Shape"}
                        onDoubleClick={() => setIsDrawing(false)}
                    >
                        <Pencil className="h-3 w-3 md:h-4 md:w-4" />
                    </Button>

                    <Button 
                        size="icon" 
                        className="h-7 w-7 md:h-9 md:w-9 shrink-0"
                        variant={isAnnotatorActive ? "default" : "secondary"} 
                        onClick={() => {
                            setIsAnnotatorActive(!isAnnotatorActive);
                            setSelectedId(null);
                            setAnnotatedObject(null);
                        }} 
                        title={isAnnotatorActive ? "Hide Annotator" : "Annotate 3D Object"}
                    >
                        <MousePointer2 className="h-3 w-3 md:h-4 md:w-4" />
                    </Button>
                    
                    {/* Record Button */}
                    <Button 
                        size="icon" 
                        className={cn("h-7 w-7 md:h-9 md:w-9 shrink-0", isRecording && "bg-red-500 hover:bg-red-600 text-white animate-pulse")}
                        variant={isRecording ? "destructive" : "secondary"} 
                        onClick={handleRecord} 
                        title={isRecording ? "Stop Recording" : "Record Scene"}
                    >
                        {isRecording ? <div className="h-3 w-3 rounded-sm bg-white" /> : <div className="h-3 w-3 rounded-full bg-red-500" />}
                    </Button>
                </div>

                {/* Non-Scrollable Actions Part (to avoid clipping dropdowns) */}
                <div className="flex gap-2 items-center shrink-0">
                    {/* Export Options Dropdown */}
                    <div className="relative shrink-0">
                        <div className="flex gap-1">
                            {isGlobalRecording && (
                                <Button size="icon" variant="destructive" onClick={() => handleRecord()} title="Stop Recording" className="animate-pulse h-7 w-7 md:h-9 md:w-9">
                                    <BoxIcon className="h-3 w-3 md:h-4 md:w-4 fill-current" />
                                </Button>
                            )}
                            <Button size="icon" className="h-7 w-7 md:h-9 md:w-9" variant={showExportMenu ? "default" : "secondary"} title="Export" onClick={() => setShowExportMenu(!showExportMenu)}><Download className="h-3 w-3 md:h-4 md:w-4" /></Button>
                        </div>
                        {showExportMenu && (
                            <div className="absolute right-0 top-full mt-2 w-48 glass-panel rounded-lg p-1 z-[100] animate-in fade-in zoom-in-95 duration-200 shadow-2xl">
                                <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={() => { handleExportGLTF(); setShowExportMenu(false); }}>
                                    <BoxIcon className="mr-2 h-3 w-3" /> Export GLTF
                                </Button>
                                <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={() => { handleExportOBJ(); setShowExportMenu(false); }}>
                                    <FileUp className="mr-2 h-3 w-3" /> Export OBJ
                                </Button>
                                <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={() => { handleExportBundle(); setShowExportMenu(false); }}>
                                    <Code className="mr-2 h-3 w-3" /> Export Bundle
                                </Button>
                                <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={() => { handleExportEnv(); setShowExportMenu(false); }}>
                                    <LayersIcon className="mr-2 h-3 w-3" /> Export Env
                                </Button>
                                <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={() => { handleSnapshot(); setShowExportMenu(false); }}>
                                    <Camera className="mr-2 h-3 w-3" /> Snapshot
                                </Button>
                            </div>
                        )}
                    </div>
                    
                    <Button size="icon" className="h-7 w-7 md:h-9 md:w-9 shrink-0" variant="secondary" onClick={() => setShowCode(!showCode)} title="View Code"><Code className="h-3 w-3 md:h-4 md:w-4" /></Button>
                    <Button 
                        size="icon" 
                        className="h-7 w-7 md:h-9 md:w-9 shrink-0 opacity-50" 
                        variant="default"
                        onClick={() => setShowShareModal(true)}
                        title="Share (Coming Soon)"
                    >
                        <Share2 className="h-3 w-3 md:h-4 md:w-4" />
                    </Button>
                </div>
            </div>
        </div>
      </div>

      {/* Code View Panel */}
      {showCode && (
        <div className="absolute inset-x-2 top-[5px] bottom-[85px] md:inset-4 z-[100] glass-panel rounded-lg p-4 text-xs font-mono text-muted-foreground flex flex-col gap-2 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-4 overflow-hidden">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-white text-sm shrink-0">Paradox 3D Code</span>
                    </div>
                    <div className="flex bg-black/30 rounded p-0.5 overflow-x-auto no-scrollbar">
                        <button 
                            className={cn("px-3 py-1 rounded text-[10px] transition-colors whitespace-nowrap", activeCodeFile === 'js' ? "bg-white text-black" : "text-gray-400 hover:text-white")}
                            onClick={() => setActiveCodeFile('js')}
                        >
                            scene.js
                        </button>
                        <button 
                            className={cn("px-3 py-1 rounded text-[10px] transition-colors whitespace-nowrap", activeCodeFile === 'html' ? "bg-white text-black" : "text-gray-400 hover:text-white")}
                            onClick={() => setActiveCodeFile('html')}
                        >
                            index.html
                        </button>
                        <button 
                            className={cn("px-3 py-1 rounded text-[10px] transition-colors whitespace-nowrap", activeCodeFile === 'css' ? "bg-white text-black" : "text-gray-400 hover:text-white")}
                            onClick={() => setActiveCodeFile('css')}
                        >
                            style.css
                        </button>
                    </div>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => {
                        setUserEdits(prev => {
                            const next = { ...prev };
                            delete next[activeCodeFile];
                            return next;
                        });
                    }} title="Revert to Generated"><Undo className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setShowCode(false)}><X className="h-3 w-3" /></Button>
                </div>
            </div>
            <div className="relative flex-1">
                <textarea 
                    value={codeContent}
                    onChange={(e) => setCodeContent(e.target.value)}
                    className="w-full h-full bg-black/50 p-4 rounded resize-none focus:outline-none focus:ring-1 focus:ring-primary text-sm font-mono text-gray-300 leading-relaxed"
                    spellCheck={false}
                />
                {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
            </div>
            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={handleSaveScene} className="gap-1 px-2 text-white hover:bg-white/10">
                    <Save className="w-3 h-3" />
                    <span>Save</span>
                </Button>
                <Button size="sm" variant="secondary" onClick={() => {
                    setUserEdits(prev => {
                        const next = { ...prev };
                        delete next[activeCodeFile];
                        return next;
                    });
                }}>Revert</Button>
                <Button size="sm" onClick={handleUpdateCode} className="gap-2">
                    <RefreshCw className="w-3 h-3" /> Update
                </Button>
            </div>
        </div>
      )}

      {/* Canvas Container */}
      <div 
        ref={containerRef} 
        className={cn("relative overflow-hidden rounded-lg shadow-2xl border border-white/10 transition-all duration-300 bg-neutral-900", getContainerStyle())}
      >
        <VantaBackground type={background} config={finalVantaConfig} />
        <Canvas 
            className="hue-exclude"
            shadows 
            camera={{ position: [0, 0, 5], fov: 50 }} 
            gl={{ preserveDrawingBuffer: true, alpha: true }}
        >
            <CameraCapture cameraRef={cameraRef} />
            <LightingController type={lighting} />
            <BackgroundController type={background} speed={backgroundSpeed} lightColor={backgroundLightColor} darkColor={backgroundDarkColor} vantaConfig={vantaConfig} />
            
            <Suspense fallback={null}>
            <group ref={sceneRef}>
                {objects.map(obj => (
                    <AnimatedMesh 
                        key={obj.id} 
                        obj={obj} 
                        isSelected={selectedId === obj.id}
                        objects={objects}
                        isAnnotatorActive={isAnnotatorActive}
                        onHover={(obj) => setHoveredObject(obj)}
                        onAnnotateSelect={(obj) => {
                            setAnnotatedObject({
                                id: obj.id,
                                name: obj.name || obj.type,
                                type: obj.type,
                                position: obj.position,
                                rotation: obj.rotation,
                                scale: obj.scale,
                                material: obj.material
                            });
                        }}
                        onSelect={() => {
                            if (!isAnnotatorActive) {
                                setSelectedId(obj.id);
                            }
                        }}
                        onUpdate={(updates) => {
                            // We need to update the specific object, not just the selected one
                            // Although only the selected one has controls, so it should be fine.
                            // But for correctness, let's update by ID.
                            const newObjects = objects.map(o => {
                                if (o.id === obj.id) {
                                    return { ...o, ...updates };
                                }
                                return o;
                            });
                            // We can't use setObjects directly if we want history support
                            // But updateSelectedObject uses selectedId.
                            // Since we are dragging the *selected* object (controls only show if selected),
                            // we can use updateSelectedObject if obj.id === selectedId.
                            if (obj.id === selectedId) {
                                updateSelectedObject(updates);
                            } else {
                                // Fallback for safety, though unlikely to be reached with current UI
                                addToHistory(newObjects);
                            }
                        }}
                        showTransformControls={showAxis && !isAnnotatorActive}
                        transformMode={transformMode}
                    />
                ))}
                
                {/* 3D Task Markers */}
                {tasks.map((task, index) => (
                    task.position3D && !task.completed && (
                        <Html position={task.position3D} key={task.id} center>
                            <div className="group relative">
                                <div className="w-6 h-6 rounded-full bg-[rgba(0,0,0,0.98)] border border-white/20 flex items-center justify-center text-white text-[10px] font-bold shadow-lg cursor-pointer hover:scale-110 transition-transform z-40"
                                     onClick={(e) => { e.stopPropagation(); /* TODO: Open task details */ }}>
                                    {index + 1}
                                </div>
                                {/* Tooltip on hover */}
                                <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 px-3 py-1.5 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                    {task.text}
                                </div>
                            </div>
                        </Html>
                    )
                ))}

                {/* Hover Label for Annotator */}
                {isAnnotatorActive && hoveredObject && !annotatedObject && (
                    <Html position={hoveredObject.position} center>
                        <div className="px-3 py-1.5 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-full shadow-lg text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 pointer-events-none whitespace-nowrap z-50 animate-in fade-in zoom-in-95 duration-200">
                             <div className="w-2 h-2 rounded-full bg-white animate-pulse shadow-[0_0_5px_#ffffff]" />
                             {hoveredObject.type}
                        </div>
                    </Html>
                )}

            </group>
            {/* Default environment if not overridden by lighting */}
            {lighting === 'studio' && (
                <EnvironmentErrorBoundary>
                    <Environment preset="city" />
                </EnvironmentErrorBoundary>
            )}
            
            {/* Real Shadow Plane */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
                <planeGeometry args={[100, 100]} />
                <shadowMaterial transparent opacity={0.4} />
            </mesh>
            
            {/* Drawing Overlay */}
            <DrawingCanvas isDrawing={isDrawing} onDrawComplete={handleDrawComplete} />

            </Suspense>
            
            <OrbitControls 
                ref={orbitControlsRef}
                makeDefault 
                enabled={!isDrawing} 
            />
        </Canvas>
      </div>

      {/* 3D Annotator Overlay */}
      {isAnnotatorActive && annotatedObject && (
          <>
            <div className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-[1px]" onClick={() => setAnnotatedObject(null)} />
            <Object3DAnnotator 
                selectedObject={annotatedObject}
                onClose={() => setAnnotatedObject(null)}
                onAnnotate={handleAnnotate}
                onAskAI={handleAskAI}
                onSendToChat={handleSendToChat}
                isGenerating={isGenerating}
            />
          </>
      )}

      {/* To-Do List Overlay - Always visible when tasks exist or annotator is active */}
      {(tasks.length > 0 || isAnnotatorActive) && (
        <TodoOverlay 
            tasks={tasks}
            onToggleTask={(id) => setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t))}
            onClear={() => setTasks([])}
            position={todoPosition}
            onPositionChange={setTodoPosition}
            isMinimized={isTodoMinimized}
            onMinimizeChange={setIsTodoMinimized}
        />
      )}

      {/* AI Chat Overlay — disabled: this project ships its own composer */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && (
      <div className={cn(
          "absolute bottom-[146px] md:bottom-8 left-0 right-0 mx-auto z-[60] w-full max-w-xl px-4 transition-all duration-300",
          showMobileMenu && "blur-md pointer-events-none opacity-50",
          isAiBarMinimized ? "h-0 overflow-visible" : ""
      )}>
          {/* Minimize Toggle (Mobile Only) */}
          <div className="md:hidden absolute -top-10 right-4 z-[70]">
               <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full shadow-lg" onClick={() => setIsAiBarMinimized(!isAiBarMinimized)}>
                   {isAiBarMinimized ? <Wand2 className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
               </Button>
          </div>

                {!isAiBarMinimized && (
              <>
                {aiMessage && (
                    <div className="relative mb-2 p-3 rounded-lg bg-black/80 border border-primary/30 text-xs text-white shadow-lg animate-in slide-in-from-bottom-2 fade-in">
                        <button 
                            onClick={() => setAiMessage(null)}
                            className="absolute top-2 right-2 p-1 text-white hover:text-white transition-colors rounded-full hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                        >
                            <X className="h-3 w-3" />
                        </button>
                        <span className="font-bold text-primary block mb-1 pr-6">Matilda:</span>
                        <div className="pr-6">{aiMessage}</div>
                    </div>
                )}
                <div className="rounded-xl glass-panel p-2 shadow-2xl border border-white/10 bg-black/40 backdrop-blur-xl">
                    <div className="flex gap-2">

                        {/* Agent Selector */}
                        <div className="relative" ref={selectorRef}>
                            <button 
                                onClick={() => setIsSelectorOpen(!isSelectorOpen)}
                                className="h-10 px-2 rounded-lg bg-[rgba(0,0,0,0.98)] text-white hover:bg-white/10 transition-colors flex items-center gap-1 text-xs font-bold border border-white/20"
                            >
                                <Bot className="h-4 w-4" />
                                <span className="hidden sm:inline">{selectedModel.split('-')[0]}</span>
                                <ChevronDown className={cn("h-3 w-3 opacity-50 transition-transform", isSelectorOpen ? "rotate-180" : "")} />
                            </button>
                            
                            {isSelectorOpen && (
                                <div className="absolute bottom-full left-0 mb-2 w-48 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
                                    <div className="p-1 space-y-0.5">
                                        <button onClick={() => handleModelSelect('gemini-3.1-flash-lite-preview')} className={cn("w-full text-left px-2 py-1.5 rounded text-xs hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20 transition-colors flex items-center justify-between", selectedModel === 'gemini-3.1-flash-lite-preview' ? "text-white" : "text-white")}>
                                            <span>Gemini Flash</span>
                                            {selectedModel === 'gemini-3.1-flash-lite-preview' && <Check className="h-3 w-3" />}
                                        </button>
                                        <button onClick={() => handleModelSelect('gemini-3.1-pro-preview')} className={cn("w-full text-left px-2 py-1.5 rounded text-xs hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20 transition-colors flex items-center justify-between", selectedModel === 'gemini-3.1-pro-preview' ? "text-white" : "text-white")}>
                                            <span>Gemini Pro</span>
                                            {selectedModel === 'gemini-3.1-pro-preview' && <Check className="h-3 w-3" />}
                                        </button>
                                        
                                        {/* Premium Models - Disabled for Unauthenticated */}
                                        <button 
                                            onClick={() => user && handleModelSelect('gpt-5.5')} 
                                            disabled={!user}
                                            className={cn("w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between", 
                                                selectedModel === 'gpt-5.5' ? "text-white" : "text-white",
                                                !user ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                                            )}
                                        >
                                            <span>GPT 5.5</span>
                                            {selectedModel === 'gpt-5.5' && <Check className="h-3 w-3" />}
                                            {!user && <span className="text-[9px] text-white ml-2">PRO</span>}
                                        </button>
                                        <button 
                                            onClick={() => user && handleModelSelect('gpt-5.4')} 
                                            disabled={!user}
                                            className={cn("w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between", 
                                                selectedModel === 'gpt-5.4' ? "text-white" : "text-white",
                                                !user ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                                            )}
                                        >
                                            <span>GPT 5.4</span>
                                            {selectedModel === 'gpt-5.4' && <Check className="h-3 w-3" />}
                                            {!user && <span className="text-[9px] text-white ml-2">PRO</span>}
                                        </button>
                                        <button 
                                            onClick={() => user && handleModelSelect('claude-4-6-sonnet')} 
                                            disabled={!user}
                                            className={cn("w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between", 
                                                selectedModel === 'claude-4-6-sonnet' ? "text-white" : "text-white",
                                                !user ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                                            )}
                                        >
                                            <span>Claude 4.6 Sonnet</span>
                                            {selectedModel === 'claude-4-6-sonnet' && <Check className="h-3 w-3" />}
                                            {!user && <span className="text-[9px] text-white ml-2">PRO</span>}
                                        </button>
                                        <button 
                                            onClick={() => user && handleModelSelect('claude-4-7-opus')} 
                                            disabled={!user}
                                            className={cn("w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between", 
                                                selectedModel === 'claude-4-7-opus' ? "text-white" : "text-white",
                                                !user ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                                            )}
                                        >
                                            <span>Claude 4.7 Opus</span>
                                            {selectedModel === 'claude-4-7-opus' && <Check className="h-3 w-3" />}
                                            {!user && <span className="text-[9px] text-white ml-2">PRO</span>}
                                        </button>
                                        <button 
                                            onClick={() => user && handleModelSelect('grok-4-3')} 
                                            disabled={!user}
                                            className={cn("w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between", 
                                                selectedModel === 'grok-4-3' ? "text-white" : "text-white",
                                                !user ? "opacity-50 cursor-not-allowed" : "hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20"
                                            )}
                                        >
                                            <span>Grok 4.3</span>
                                            {selectedModel === 'grok-4-3' && <Check className="h-3 w-3" />}
                                            {!user && <span className="text-[9px] text-white ml-2">PRO</span>}
                                        </button>

                                        {/* Offline Models */}
                                        <div className="my-1 border-t border-white/20" />
                                        <button disabled className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between opacity-50 cursor-not-allowed text-white">
                                            <span>Gemma E4B</span>
                                            <span className="text-[7px] bg-[rgba(0,0,0,0.98)] dark:bg-black/5 bg-[rgba(0,0,0,0.98)]/10 text-white px-1.5 rounded uppercase font-bold border border-white/20">COMING SOON</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

            {/* API Key Help Modal */}
            {showApiKeyHelp && (
                <ApiKeyHelpModal
                    isOpen={showApiKeyHelp}
                    onClose={() => setShowApiKeyHelp(false)}
                    service={selectedModel.includes('gemini') ? 'gemini' : 'other'}
                />
            )}
            {/* BYOK Modal */}
            {showBYOKModal && pendingModel && (
                <BYOKSettingsModal
                    isOpen={showBYOKModal}
                    onClose={() => setShowBYOKModal(false)}
                    modelId={(pendingModel as OnlineModelType) || 'gpt-5.5'}
                    modelName={pendingModel as string}
                    onConfirm={handleBYOKConfirm}
                    isAuthenticated={!!user}
                    hue={hue}
                />
            )}

            {showAuthModal && (
                <AuthRequiredModal
                    isOpen={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                    modelName={authModalModelName}
                    hue={hue}
                />
            )}

            {/* Download Modal */}
            {showDownloadModal && downloadModelId && (
                <ModelDownloadModal
                    isOpen={showDownloadModal}
                    onClose={() => setShowDownloadModal(false)}
                    modelId={downloadModelId}
                    onDownloadComplete={handleDownloadComplete}
                    hue={hue}
                />
            )}

            {/* Imagine Background Modal */}
            <CodeyWidgetTemplate
                isOpen={showImagineModal}
                onClose={() => setShowImagineModal(false)}
                title="Imagine Background"
                hue={hue}
                icon={<Wand2 size={16} />}
            >
                <div className="p-6 space-y-4">
                    <p className="text-xs text-white">
                        Describe the background you want to generate.
                    </p>
                    <input
                        type="text"
                        className="w-full bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg px-3 py-2 text-xs text-white focus:border-white/20 outline-none"
                        placeholder="e.g. A cyberpunk city with neon lights..."
                        value={imaginePrompt}
                        onChange={(e) => setImaginePrompt(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && imaginePrompt.trim()) {
                                setShowImagineModal(false);
                                handleAIChat(`imagine ${imaginePrompt}`);
                                setImaginePrompt('');
                            }
                        }}
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <button 
                            className="px-4 py-2 rounded-xl font-bold text-xs transition-all bg-[rgba(0,0,0,0.98)] border border-white/20 text-white hover:border-white/20"
                            onClick={() => setShowImagineModal(false)}
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={() => {
                                setShowImagineModal(false);
                                handleAIChat(`imagine ${imaginePrompt}`);
                                setImaginePrompt('');
                            }}
                            disabled={!imaginePrompt.trim() || isGenerating}
                            className={`px-4 py-2 rounded-xl font-bold text-xs transition-all ${
                                !imaginePrompt.trim() || isGenerating
                                  ? 'bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20 text-white cursor-not-allowed'
                                  : 'bg-[rgba(0,0,0,0.98)] text-white hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] shadow-lg shadow-black/50'
                              }`}
                        >
                            {isGenerating ? 'Generating...' : 'Generate'}
                        </button>
                    </div>
                </div>
            </CodeyWidgetTemplate>

            <CodeyWidgetTemplate
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                title="Feature Coming Soon"
                icon={<AlertTriangle size={16} />}
                hue={hue}
                footer={
                    <div className="flex justify-end">
                        <button 
                            onClick={() => setShowShareModal(false)}
                            className="px-4 py-2 bg-[rgba(0,0,0,0.98)] text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[rgba(0,0,0,0.05)] dark:hover:bg-[rgba(0,0,0,0.98)] hover:text-white transition-colors"
                        >
                            Close
                        </button>
                    </div>
                }
            >
                <div className="p-6 text-center space-y-4">
                    <div className="w-16 h-16 bg-[rgba(0,0,0,0.98)] dark:bg-black/10 bg-[rgba(0,0,0,0.98)]/20 rounded-full flex items-center justify-center mx-auto border border-white/20">
                        <AlertTriangle size={32} className="text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Share Project</h3>
                    <p className="text-sm text-white">
                        Sharing functionality is currently in development and will be available in a future update of Paradox 3D.
                    </p>
                </div>
            </CodeyWidgetTemplate>

                        <input 
                            type="text" 
                            placeholder="Change background, create object, change color..." 
                            className="flex-1 bg-transparent px-2 text-sm outline-none placeholder:text-white text-white"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAIChat()}
                        />
                        <Button size="sm" onClick={() => handleAIChat()} disabled={isGenerating} className="bg-[rgba(0,0,0,0.98)] border border-white hover:bg-white/10 !text-white font-bold shadow-lg">
                            <MessageSquare className="mr-2 h-3 w-3" />
                            {isGenerating ? "..." : "SEND"}
                        </Button>
                    </div>
                </div>
              </>
          )}
      </div>
      )}
    </div>
  );
}
